import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeKatex from 'rehype-katex';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import html2pdf from 'html2pdf.js';
import ChatInterface from '../components/ChatInterface';
import DocumentDropZone, { DroppedDocument } from '../components/DocumentDropZone';
import ChatContextViewer from '../components/ChatContextViewer';
import HtmlPdfViewer from '../components/HtmlPdfViewer';
import { UnifiedPdfCitationViewer } from '../components/UnifiedPdfCitationViewer';
import { UniversalJsonRenderer } from '../components/UniversalJsonRenderer';
import { SuiteReportRenderer } from '../components/CyberSuiteReportRenderer';
import { isSuiteWorkflowType } from '../types/suites';
import CitationToggle from '../components/CitationToggle';
import SelectiveRegenerateModal from '../components/SelectiveRegenerateModal';
import AMS360AttachReportModal from '../components/AMS360AttachReportModal';
import GenerateProposalModal from '../components/GenerateProposalModal';
import ReportConversationsStrip from '../components/ReportConversationsStrip';
import { ams360Service } from '../services/ams360Service';
import { useCitationToggle } from '../hooks/useCitationToggle';
import { markdownTableComponents } from '../components/TableExportWrapper';
import LetterExportFlow from '../components/letter-templates/LetterExportFlow';
import { RegenerateSelection } from '../hooks/useRegenerateSelection';
import { aiService } from '../services/aiService';
import { workflowService } from '../services/workflowService';
import { BETA_TOOL_LABELS } from '../constants/betaToolLabels';

// Citation interface for compatibility
interface ParsedCitation {
  id?: string;
  quote?: string;
  text?: string;
  documentId: string;
  docId?: string;
  page?: number;
  startOffset?: number;
  endOffset?: number;
  documentName?: string;
  context?: string;
}

// Section interface for Table of Contents
interface ReportSection {
  id: string;
  title: string;
  parentId?: string;  // For hierarchical sections
  isChild?: boolean;   // For visual indentation
}

interface WorkflowReportData {
  content?: string;
  workflowType: string;
  documents: Array<{
    id: string;
    name: string;
    policyType?: string;
    size?: number;
    itemType?: string;
  }>;
  customerId?: string;
  customerName?: string;
  citations?: Array<{
    id?: string;
    type?: string;
    text: string;
    documentId?: string;
    documentName?: string;
    found?: boolean;
    startIndex?: number;
    endIndex?: number;
    pageNumber?: number;
    confidence?: number;
    // Legacy format support
    docId?: string;
    page?: number;
    start?: number;
    end?: number;
  }>;
  // Universal JSON support
  universalJsonResponse?: any;
  isUniversalJson?: boolean;
  // Additional properties for existing reports
  reportId?: string;
  reportTitle?: string;
  projectId?: string;
  createdAt?: Date;
  hideCitations?: boolean;
  updatedAt?: Date;
  additionalContext?: string;
  // Regeneration support
  isRegenerated?: boolean;
}

/**
 * Workflow types that cannot be regenerated. `research` has no source
 * documents to re-run over; the legacy types (rate_analysis, new_business,
 * certificate_of_effort) are not in the WorkflowRegistry, so regenerating them
 * used to silently produce a document_comparison report instead.
 * Keep in sync with NON_REGENERABLE_WORKFLOW_TYPES in
 * riskninja-backend/src/routes/workflows.ts.
 */
const NON_REGENERABLE_WORKFLOW_TYPES = new Set([
  'research',
  'research_analysis',
  'research_tool',
  'rate_analysis',
  'new_business',
  'certificate_of_effort',
]);

const WorkflowReport: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const reportContentRef = useRef<HTMLDivElement>(null);

  // Citation toggle state management
  const { showCitations, hideCitations, toggleCitations } = useCitationToggle();

  // Helper function to process workflow data and detect Universal JSON structure
  const processWorkflowData = (data: WorkflowReportData | null): WorkflowReportData | null => {
    if (!data) return null;

    // If already processed (has isUniversalJson flag), return as-is
    if (data.hasOwnProperty('isUniversalJson')) {
      console.log('🔍 WorkflowData already processed, isUniversalJson:', data.isUniversalJson);
      return data;
    }

    console.log('🔍 Processing workflowData to detect Universal JSON structure');

    // Check if content is structured JSON
    let isJsonContent = false;
    let jsonContent = null;

    if (typeof data.content === 'string') {
      try {
        let jsonString = data.content.trim();

        // Strip markdown code fences if present (common from AI responses)
        if (jsonString.startsWith('```json')) {
          jsonString = jsonString.replace(/^```json\s*\n?/, '').replace(/\n?```\s*$/, '');
        } else if (jsonString.startsWith('```')) {
          jsonString = jsonString.replace(/^```\s*\n?/, '').replace(/\n?```\s*$/, '');
        }

        // Look for JSON object boundaries
        const jsonStart = jsonString.indexOf('{');
        const jsonEnd = jsonString.lastIndexOf('}');
        if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
          jsonString = jsonString.substring(jsonStart, jsonEnd + 1);
        }

        // Fix trailing commas in JSON (common AI response issue)
        jsonString = jsonString.replace(/,(\s*[\]}])/g, '$1');

        jsonContent = JSON.parse(jsonString);

        // Check if this is structured data for UniversalJsonRenderer
        if (jsonContent && typeof jsonContent === 'object') {
          const hasUniversalStructure =
            jsonContent.responseMetadata ||
            jsonContent.executiveSummary ||
            jsonContent.analysisMatrices ||
            jsonContent.keyFindings ||
            jsonContent.riskManagementMatrix ||
            jsonContent.trendAnalysis ||
            jsonContent.criticalAlerts ||
            (jsonContent.namedInsuredProfile && jsonContent.actionableRecommendations) ||
            // Loss run forensic analysis structure
            jsonContent.categoryBreakdown ||
            jsonContent.severityBreakdown ||
            jsonContent.riskHeatMap ||
            jsonContent.detailedClaimsList ||
            jsonContent.lossRunSummary ||
            // Insurance requirements structure
            jsonContent.complianceMatrix ||
            // Commercial lines executive renewal structure
            jsonContent.executiveRenewalSummary ||
            jsonContent.riskIntelligenceReport ||
            // Rate analysis structure
            jsonContent.analysisTables ||
            jsonContent.driverHierarchy ||
            jsonContent.weakSignals ||
            // Portfolio analyzer structure
            jsonContent.portfolioStructure ||
            jsonContent.portfolioSynthesis ||
            jsonContent.scenarioAnalysis;

          if (hasUniversalStructure) {
            isJsonContent = true;
            console.log('✅ Detected Universal JSON structure in content');
          }
        }
      } catch (e) {
        console.log('📝 Content is not valid JSON');
      }
    } else if (typeof data.content === 'object') {
      // Already parsed JSON object
      jsonContent = data.content;
      isJsonContent = true;
      console.log('✅ Content is already a JSON object');
    }

    // Return enhanced data with Universal JSON flags
    const processedData: WorkflowReportData = {
      ...data,
      isUniversalJson: isJsonContent,
      universalJsonResponse: isJsonContent ? jsonContent : undefined
    };

    console.log('🎯 Processed workflowData:', {
      isUniversalJson: processedData.isUniversalJson,
      hasUniversalJsonResponse: !!processedData.universalJsonResponse,
      workflowType: processedData.workflowType
    });

    return processedData;
  };

  // State for loaded report data (must be declared before use)
  const [loadedReportData, setLoadedReportData] = useState<WorkflowReportData | null>(null);
  // Loading state for async operations
  const [isLoadingReportData, setIsLoadingReportData] = useState<boolean>(false);

  // State for workflow data (can be updated when reportId becomes available)
  // Process location.state data to detect Universal JSON structure
  const [workflowData, setWorkflowData] = useState<WorkflowReportData | null>(
    processWorkflowData(loadedReportData || location.state as WorkflowReportData)
  );
  
  // CRITICAL FIX: Try to recover from sessionStorage if location.state is lost
  useEffect(() => {
    if (!workflowData) {
      try {
        const storedData = sessionStorage.getItem('workflow-report-data');
        if (storedData) {
          const recoveredData = JSON.parse(storedData) as WorkflowReportData;
          // Process recovered data to set Universal JSON flags
          const processedData = processWorkflowData(recoveredData);
          setWorkflowData(processedData);
          console.log('🔄 Recovered and processed workflow data from sessionStorage');
          // Clear it after reading to avoid stale data
          sessionStorage.removeItem('workflow-report-data');
        }
      } catch (error) {
        console.error('Failed to read from sessionStorage:', error);
      }
    }
  }, [workflowData]);

  // Reset loaded data when the reportId in the URL changes so a new report can be fetched
  const currentUrlReportId = new URLSearchParams(location.search).get('reportId');
  const prevReportIdRef = useRef<string | null>(currentUrlReportId);
  useEffect(() => {
    if (currentUrlReportId && currentUrlReportId !== prevReportIdRef.current) {
      console.log(`🔄 reportId changed from ${prevReportIdRef.current} to ${currentUrlReportId}, resetting loaded data`);
      setLoadedReportData(null);
      setWorkflowData(null);
      prevReportIdRef.current = currentUrlReportId;
    }
  }, [currentUrlReportId]);

  // Update workflowData when loadedReportData changes (from URL reportId loading)
  useEffect(() => {
    if (loadedReportData) {
      // Always update if we have loaded report data, especially if current workflowData has no content
      if (!workflowData || !workflowData.content || workflowData.content === '') {
        console.log('🔄 Updating workflowData with loaded report data (empty or missing content)');
        setWorkflowData(loadedReportData);
      } else if (loadedReportData.reportId !== workflowData.reportId) {
        console.log('🔄 Updating workflowData with loaded report data (different reportId)');
        setWorkflowData(loadedReportData);
      }
    }
  }, [loadedReportData]);

  // Update workflowData with reportId from URL if available and missing
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlReportId = urlParams.get('reportId') || (location.state as any)?.reportId;

    if (urlReportId && workflowData && !workflowData?.reportId) {
      console.log('🔄 Adding reportId to workflowData from URL:', urlReportId);
      setWorkflowData(prev => prev ? { ...prev, reportId: urlReportId } : prev);
    }
  }, [location.search, location.state, workflowData]);

  // CRITICAL DEBUG - Log the actual location state to see what's happening
  console.log('🚨 CRITICAL: WorkflowReport mount debug:', {
    hasLocationState: !!location.state,
    locationStateType: typeof location.state,
    locationStateKeys: location.state ? Object.keys(location.state) : [],
    rawLocationState: location.state,
    hasLoadedReportData: !!loadedReportData,
    workflowDataExists: !!workflowData,
    recoveredFromSessionStorage: !!sessionStorage.getItem('workflow-report-data'),
    pathname: location.pathname,
    search: location.search
  });
  
  // Debug content type and context
  console.log('📊 WorkflowReport received data:', {
    hasContent: !!workflowData?.content,
    contentType: typeof workflowData?.content,
    isUniversalJson: workflowData?.isUniversalJson,
    hasUniversalJsonResponse: !!workflowData?.universalJsonResponse,
    citationsCount: workflowData?.citations?.length || 0,
    reportId: workflowData?.reportId,
    projectId: workflowData?.projectId,
    customerId: workflowData?.customerId,
    workflowType: workflowData?.workflowType,
    currentUrl: location.pathname + location.search
  });
  
  // Fallback: try to get data from URL parameters (moved to useEffect)
  useEffect(() => {
    if (!workflowData) {
      const urlParams = new URLSearchParams(location.search);
      const dataParam = urlParams.get('data');
      if (dataParam) {
        try {
          const parsedData = JSON.parse(dataParam) as WorkflowReportData;
          setWorkflowData(parsedData);
        } catch (error) {
          console.error('Error parsing workflow data from URL:', error);
        }
      }
    }
  }, [location.search]);

  // Try to get projectId from multiple sources if not in workflowData (moved to useEffect)
  useEffect(() => {
    if (workflowData && !workflowData?.projectId) {
      const urlParams = new URLSearchParams(location.search);
      let projectIdFromUrl = urlParams.get('projectId');

      // Also check for project in current path (common pattern: /project/:projectId/report/:reportId)
      const pathParts = location.pathname.split('/');
      const projectIndex = pathParts.indexOf('project');
      if (projectIndex !== -1 && projectIndex + 1 < pathParts.length) {
        projectIdFromUrl = pathParts[projectIndex + 1];
      }

      if (projectIdFromUrl) {
        setWorkflowData(prev => prev ? { ...prev, projectId: projectIdFromUrl || undefined } : prev);
        console.log(`📁 Using projectId from URL/path: ${projectIdFromUrl}`);
      }

      // If still no projectId, we can try to infer it from the report context
      // This will be handled by the backend lookup we already implemented
    }
  }, [location.search, location.pathname]);

  // Check if AMS360 is enabled for the company
  useEffect(() => {
    const checkAms360Status = async () => {
      try {
        const settings = await ams360Service.getSettings();
        setIsAms360Enabled(settings.enabled);
      } catch (error) {
        setIsAms360Enabled(false);
      }
    };
    checkAms360Status();
  }, []);

  // Handle case when workflowData is null but we have projectId in navigation state
  useEffect(() => {
    if (!workflowData && location.state) {
      const navState = location.state as any;
      if (navState.projectId) {
        console.log(`📁 Creating initial workflowData with projectId from navigation state: ${navState.projectId}`);
        setWorkflowData({
          projectId: navState.projectId,
          customerId: navState.customerId,
          reportId: navState.reportId
        } as WorkflowReportData);
      }
    }
  }, [location.state]);

  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showLetterExportFlow, setShowLetterExportFlow] = useState(false);
  const [chatHistory, setChatHistory] = useState<any[]>([]);
  const [showHtmlPdfViewer, setShowHtmlPdfViewer] = useState(false);

  // Table of Contents sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [railOpen, setRailOpen] = useState(true);
  // Portal target for ExportControls rendered by UniversalJsonRenderer —
  // lets the export buttons live in the outer header next to the citations
  // toggle instead of in a separate toolbar above the report content.
  const exportSlotRef = useRef<HTMLDivElement | null>(null);
  // After the report renders, we filter reportSections down to entries whose
  // target IDs actually exist in the DOM. Several workflows (notably
  // loss_run_analyzer_v4) push TOC entries from data the renderer doesn't
  // surface, producing dead links. Null means "not measured yet" — render
  // everything until we know better.
  const [validSectionIds, setValidSectionIds] = useState<Set<string> | null>(null);
  const [htmlViewerCitation, setHtmlViewerCitation] = useState<any>(null);
  const [htmlViewerDocumentUrl, setHtmlViewerDocumentUrl] = useState('');
  const [allCitationsOnPage, setAllCitationsOnPage] = useState<any[]>([]);
  const [clickedCitation, setClickedCitation] = useState<any>(null);
  const [highlight, setHighlight] = useState<any>(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [selectedCitation, setSelectedCitation] = useState<any>(null);
  const [additionalDocuments, setAdditionalDocuments] = useState<DroppedDocument[]>([]);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateProgress, setRegenerateProgress] = useState(0);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  // Set when the backend generated a report but failed to save it — the
  // content lives only in this page until the user exports it.
  const [showUnsavedWarning, setShowUnsavedWarning] = useState<boolean>(
    !!(location.state as any)?.reportNotPersisted
  );

  // Navigating from one report to another keeps this component mounted, so
  // the flag has to follow location.state rather than only the initial value.
  useEffect(() => {
    setShowUnsavedWarning(!!(location.state as any)?.reportNotPersisted);
  }, [location.state]);

  // Legacy/registry-less workflow types can't go through the regeneration
  // pipeline — see NON_REGENERABLE_WORKFLOW_TYPES.
  const canRegenerate = !NON_REGENERABLE_WORKFLOW_TYPES.has(
    workflowData?.workflowType || ''
  );
  const [showAMS360AttachModal, setShowAMS360AttachModal] = useState(false);
  const [isAms360Enabled, setIsAms360Enabled] = useState(false);
  const [showGenerateProposal, setShowGenerateProposal] = useState(false);

  // Context management state
  const [contextStats, setContextStats] = useState<any>(null);
  const [includeReport, setIncludeReport] = useState(true);
  const [includedDocuments, setIncludedDocuments] = useState<Set<string>>(new Set());
  const [includeChatHistory, setIncludeChatHistory] = useState(true);
  const [pastReports, setPastReports] = useState<Array<{ id: string; title: string }>>([]);
  const [includedPastReports, setIncludedPastReports] = useState<Set<string>>(new Set());

  // Chat session auto-save state
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [sessionTitle, setSessionTitle] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error'>('saved');
  const [isFirstMessage, setIsFirstMessage] = useState(true);
  const [forceNewSession, setForceNewSession] = useState(false);

  // Extract sections from the report for Table of Contents
  const reportSections = useMemo<ReportSection[]>(() => {
    if (!workflowData) return [];

    const sections: ReportSection[] = [];

    // Helper function to create section ID from title
    const createSectionId = (title: string) =>
      title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

    try {
      // Try to parse content as JSON to detect Universal JSON format
      let parsedContent: any = null;
      if (typeof workflowData.content === 'string') {
        try {
          parsedContent = JSON.parse(workflowData.content);
        } catch (e) {
          // Not JSON, will handle as markdown below
        }
      }

      // Handle Universal JSON format - check both explicit flag and parsed structure
      const universalData = workflowData.universalJsonResponse || parsedContent;
      if (universalData && (workflowData.isUniversalJson || parsedContent)) {
        const data = universalData;

        // Add standard Universal JSON sections
        if (data.executiveSummary) {
          sections.push({ id: 'executive-summary', title: 'Executive Summary' });

          // Add subsections for loss run forensic analysis structure (OLD format)
          const lossRunSummary = data.executiveSummary as any;
          if (lossRunSummary.portfolioOverview || lossRunSummary.financialSummary || lossRunSummary.extractionQuality) {
            // This is a loss run forensic structure - add subsections
            if (lossRunSummary.portfolioOverview) {
              sections.push({ id: 'portfolio-overview', title: '  Portfolio Overview', isChild: true });
            }
            if (lossRunSummary.financialSummary) {
              sections.push({ id: 'financial-summary', title: '  Financial Summary', isChild: true });
            }
            if (lossRunSummary.keyRiskInsights) {
              sections.push({ id: 'key-risk-insights', title: '  Key Risk Insights', isChild: true });
            }
            if (lossRunSummary.extractionQuality) {
              sections.push({ id: 'extraction-quality', title: '  Extraction Quality', isChild: true });
            }
          }
          // Add subsections for SIMPLIFIED loss run structure (flat fields)
          else if (lossRunSummary.totalClaims || lossRunSummary.totalIncurred || lossRunSummary.totalPaid) {
            sections.push({ id: 'portfolio-overview', title: '  Portfolio Overview', isChild: true });
            sections.push({ id: 'financial-summary', title: '  Financial Summary', isChild: true });
          }
          // Add subsections for NEW loss_run_analyzer structure (portfolioSnapshot, keyFindings, lossDrivers)
          else if (lossRunSummary.portfolioSnapshot || lossRunSummary.keyFindings || lossRunSummary.lossDrivers) {
            if (lossRunSummary.portfolioSnapshot) {
              sections.push({ id: 'portfolio-snapshot', title: '  Portfolio Snapshot', isChild: true });
            }
            if (lossRunSummary.keyFindings && lossRunSummary.keyFindings.length > 0) {
              sections.push({ id: 'key-findings', title: '  Key Findings', isChild: true });
            }
            if (lossRunSummary.lossDrivers && lossRunSummary.lossDrivers.length > 0) {
              sections.push({ id: 'loss-drivers', title: '  Loss Drivers', isChild: true });
            }
            if (lossRunSummary.trendAnalysis) {
              sections.push({ id: 'trend-analysis', title: '  Trend Analysis', isChild: true });
            }
            if (lossRunSummary.analystNotes) {
              sections.push({ id: 'analyst-notes', title: '  Analyst Notes', isChild: true });
            }
          }
          // Add subsections for YOY Renewal structure
          else if (lossRunSummary.renewalOverview || lossRunSummary.recommendedPathway) {
            if (lossRunSummary.analysisTrack) {
              sections.push({ id: 'analysis-track', title: '  Analysis Track', isChild: true });
            }
            if (lossRunSummary.renewalOverview) {
              sections.push({ id: 'renewal-overview', title: '  Renewal Overview', isChild: true });
            }
            if (lossRunSummary.financialSummary) {
              sections.push({ id: 'financial-summary-yoy', title: '  Financial Summary', isChild: true });
            }
            if (lossRunSummary.topAdvantages && lossRunSummary.topAdvantages.length > 0) {
              sections.push({ id: 'top-advantages', title: '  Top Advantages', isChild: true });
            }
            if (lossRunSummary.topConcerns && lossRunSummary.topConcerns.length > 0) {
              sections.push({ id: 'top-concerns', title: '  Top Concerns', isChild: true });
            }
            if (lossRunSummary.strategicTradeOff) {
              sections.push({ id: 'strategic-trade-off', title: '  Strategic Trade-Off', isChild: true });
            }
            if (lossRunSummary.recommendation) {
              sections.push({ id: 'recommendation', title: '  Recommendation', isChild: true });
            }
            if (lossRunSummary.recommendedPathway) {
              sections.push({ id: 'recommended-pathway', title: '  Recommended Pathway', isChild: true });
            }
          }
        }

        // Check for loss_run_analyzer specific sections
        const isLossRunAnalyzer = data.responseMetadata?.workflowType === 'loss_run_analyzer' ||
          ((data as any).documentManifest && (data as any).claims && (data as any).validationResults);
        if (isLossRunAnalyzer) {
          if ((data as any).documentManifest && (data as any).documentManifest.length > 0) {
            sections.push({ id: 'document-manifest', title: 'Document Manifest' });
          }
          if ((data as any).claims && (data as any).claims.length > 0) {
            sections.push({ id: 'claims-table', title: 'Claims' });
          }
          if ((data as any).validationResults && (data as any).validationResults.length > 0) {
            sections.push({ id: 'validation-results', title: 'Validation Results' });
          }
          if ((data as any).claimDevelopment && (data as any).claimDevelopment.length > 0) {
            sections.push({ id: 'claim-development', title: 'Claim Development' });
          }
          if ((data as any).frequencyAnalysis) {
            sections.push({ id: 'frequency-analysis', title: 'Frequency Analysis' });
            if ((data as any).frequencyAnalysis.claimsByLOB && (data as any).frequencyAnalysis.claimsByLOB.length > 0) {
              sections.push({ id: 'claims-by-lob', title: '  Claims by LOB', isChild: true });
            }
            if ((data as any).frequencyAnalysis.claimsByState && (data as any).frequencyAnalysis.claimsByState.length > 0) {
              sections.push({ id: 'geographic-distribution', title: '  Geographic Distribution', isChild: true });
            }
          }
          if ((data as any).severityAnalysis) {
            sections.push({ id: 'severity-analysis', title: 'Severity Analysis' });
          }
          if ((data as any).topClaims && (data as any).topClaims.length > 0) {
            sections.push({ id: 'top-claims', title: 'Top Claims' });
          }
          if ((data as any).dataQuality) {
            sections.push({ id: 'data-quality', title: 'Data Quality' });
          }
        }

        // Check for SIMPLIFIED loss run schema sections (NEW format)
        const isSimplifiedLossRun = (data as any).claimsSummary || (data as any).categoryAnalysis || (data as any).severityAnalysis;
        if (isSimplifiedLossRun) {
          if ((data as any).claimsSummary && (data as any).claimsSummary.length > 0) {
            sections.push({ id: 'claims-summary', title: 'Claims Summary' });
          }
          if ((data as any).categoryAnalysis && (data as any).categoryAnalysis.length > 0) {
            sections.push({ id: 'category-analysis', title: 'Category Analysis' });
          }
          if ((data as any).severityAnalysis && (data as any).severityAnalysis.length > 0) {
            sections.push({ id: 'severity-analysis', title: 'Severity Analysis' });
          }
          if ((data as any).trendAnalysis) {
            sections.push({ id: 'trend-analysis-simplified', title: 'Trend Analysis' });
          }
          if ((data as any).riskAssessment) {
            sections.push({ id: 'risk-assessment-simplified', title: 'Risk Assessment' });
          }
          if ((data as any).narrative) {
            sections.push({ id: 'narrative', title: 'Analysis Narrative' });
            if ((data as any).narrative.keyFindings && (data as any).narrative.keyFindings.length > 0) {
              sections.push({ id: 'key-findings-narrative', title: '  Key Findings', isChild: true });
            }
            if ((data as any).narrative.concerns && (data as any).narrative.concerns.length > 0) {
              sections.push({ id: 'concerns-narrative', title: '  Concerns', isChild: true });
            }
            if ((data as any).narrative.positiveIndicators && (data as any).narrative.positiveIndicators.length > 0) {
              sections.push({ id: 'positive-indicators-narrative', title: '  Positive Indicators', isChild: true });
            }
          }
        }

        // Add analysis matrices as sections (Detailed Analysis) - skip for simplified loss run
        if (!isSimplifiedLossRun) {
          if (data.analysisMatrices && Array.isArray(data.analysisMatrices) && data.analysisMatrices.length > 0) {
            sections.push({ id: 'analysis-matrices', title: 'Analysis Matrices' });
            data.analysisMatrices.forEach((matrix: any, index: number) => {
              if (matrix.title) {
                sections.push({
                  id: `matrix-${createSectionId(matrix.title)}` || `matrix-${index}`,
                  title: `  ${matrix.title}`,
                  parentId: 'analysis-matrices',
                  isChild: true
                });
              }
            });
          } else if ((data as any).categoryBreakdown || (data as any).severityBreakdown || (data as any).riskHeatMap) {
            // Loss run forensic analysis - breakdown tables instead of matrices
            sections.push({ id: 'analysis-matrices', title: 'Analysis Matrices' });
            if ((data as any).categoryBreakdown && Object.keys((data as any).categoryBreakdown).length > 0) {
              sections.push({ id: 'category-breakdown', title: '  Category Breakdown', isChild: true });
            }
            if ((data as any).severityBreakdown && Object.keys((data as any).severityBreakdown).length > 0) {
              sections.push({ id: 'severity-breakdown', title: '  Severity Breakdown', isChild: true });
            }
            if ((data as any).riskHeatMap && Object.keys((data as any).riskHeatMap).length > 0) {
              sections.push({ id: 'risk-heat-map', title: '  Risk Heat Map', isChild: true });
            }
          }

          if (data.keyFindings) {
            sections.push({ id: 'key-findings', title: 'Key Findings' });
          } else if ((data as any).criticalClaims || (data as any).processingLog || (data as any).recommendations) {
            // Loss run forensic analysis - critical claims and recommendations
            sections.push({ id: 'key-findings', title: 'Key Findings' });
            if ((data as any).criticalClaims && (data as any).criticalClaims.length > 0) {
              sections.push({ id: 'critical-claims', title: '  Critical Claims', isChild: true });
            }
            if ((data as any).processingLog && (data as any).processingLog.length > 0) {
              sections.push({ id: 'processing-log', title: '  Processing Log', isChild: true });
            }
            if ((data as any).recommendations && (data as any).recommendations.length > 0) {
              sections.push({ id: 'loss-run-recommendations', title: '  Recommendations', isChild: true });
            }
            if ((data as any).analysisNarrative) {
              sections.push({ id: 'analysis-narrative', title: '  Analysis Narrative', isChild: true });
            }
          }

          // Add Detailed Claims List for loss run analysis (OLD format)
          if ((data as any).detailedClaimsList && (data as any).detailedClaimsList.length > 0) {
            sections.push({ id: 'detailed-claims-list', title: 'Detailed Claims List' });
          }
        }
        if (data.strategicConsiderations && data.strategicConsiderations.length > 0) {
          sections.push({ id: 'strategic-considerations', title: 'Strategic Considerations' });
        }
        if (data.riskMitigationStrategies && data.riskMitigationStrategies.length > 0) {
          sections.push({ id: 'risk-mitigation-strategies', title: 'Risk Mitigation Strategies' });
        }
        if (data.riskManagementMatrix && data.riskManagementMatrix.recommendations && data.riskManagementMatrix.recommendations.length > 0) {
          sections.push({ id: 'risk-management-matrix', title: data.riskManagementMatrix.title || 'Risk Management Matrix' });
        }
        // Add old-format trend analysis only if not simplified loss run (already added as trend-analysis-simplified)
        if (data.trendAnalysis && !isSimplifiedLossRun) {
          sections.push({ id: 'trend-analysis', title: 'Trend Analysis' });
        }
        if (data.benchmarkingInsights) {
          sections.push({ id: 'benchmarking-insights', title: 'Benchmarking Insights' });
        }

        // Debug log for cost-benefit analysis
        console.log('🔍 Cost-Benefit Analysis check:', {
          hasCostBenefitAnalysis: !!data.costBenefitAnalysis,
          isArray: Array.isArray(data.costBenefitAnalysis),
          length: data.costBenefitAnalysis?.length,
          data: data.costBenefitAnalysis
        });

        if (data.costBenefitAnalysis && data.costBenefitAnalysis.length > 0) {
          sections.push({ id: 'cost-benefit-analysis', title: 'Cost-Benefit Analysis' });
          console.log('✅ Added cost-benefit-analysis section to sidebar');
        }
        if (data.dataQualityAssessment) {
          sections.push({ id: 'data-quality-assessment', title: 'Data Quality Assessment' });
        }
        if (data.recommendations && data.recommendations.length > 0) {
          sections.push({ id: 'recommendations', title: 'Recommendations' });
        }
        if (data.renewalRecommendations && data.renewalRecommendations.recommendations) {
          sections.push({ id: 'renewal-recommendations', title: 'Renewal Recommendations' });
        }
        if ((data as any).marketAnalysis && ((data as any).marketAnalysis.marketConditions || (data as any).marketAnalysis.competitivePosition)) {
          sections.push({ id: 'market-analysis', title: 'Market Analysis' });
        }
        if ((data as any).renewalStrategy) {
          sections.push({ id: 'renewal-strategy', title: 'Renewal Strategy' });
        }
        if ((data as any).clauseWordingComparison && ((data as any).clauseWordingComparison.comparisons || (data as any).clauseWordingComparison.analysis)) {
          sections.push({ id: 'clause-wording-comparison', title: 'Clause Wording Comparison' });
        }
        if (data.namedInsuredProfile) {
          sections.push({ id: 'named-insured-profile', title: 'Named Insured Profile' });
        }
        if (data.strategicRecommendations) {
          sections.push({ id: 'strategic-recommendations', title: 'Strategic Recommendations' });
        }
        if (data.supportingDocumentation) {
          sections.push({ id: 'supporting-documentation', title: 'Supporting Documentation' });
        }

        // Add Risk-Shift Navigator and its subsections
        if ((data as any).riskShift) {
          sections.push({ id: 'risk-shift-navigator', title: 'Risk-Shift Navigator' });

          // Add subsections if they exist
          if ((data as any).riskShift.detailedComparisonTable) {
            sections.push({ id: 'detailed-change-analysis', title: '  Detailed Change Analysis' });
          }
          if ((data as any).riskShift.metaAnalysis) {
            sections.push({ id: 'meta-analysis-strategic-insights', title: '  Meta-Analysis & Strategic Insights' });
          }
        }

        // Add Non-Standard Provisions
        if (data.nonStandardProvisions && data.nonStandardProvisions.identified && data.nonStandardProvisions.provisions && data.nonStandardProvisions.provisions.length > 0) {
          sections.push({ id: 'non-standard-provisions', title: 'Non-Standard Provisions' });
        }

        // Add Insurance Requirements specific sections
        if (data.responseMetadata?.workflowType === 'insurance_requirements') {
          // Add parent section
          sections.push({ id: 'insurance-requirements', title: 'Insurance Requirements Analysis' });

          // Add Executive Summary subsection for insurance requirements
          if (data.executiveSummary && (data.executiveSummary as any).summary) {
            sections.push({ id: 'insurance-executive-summary', title: '  Executive Summary' });
          }

          // Add TL;DR subsection
          if ((data as any).tldr) {
            sections.push({ id: 'tldr', title: '  TL;DR' });
          }

          // Add Compliance Table subsection
          if ((data as any).complianceTable && Array.isArray((data as any).complianceTable) && (data as any).complianceTable.length > 0) {
            sections.push({ id: 'compliance-table', title: '  Compliance Analysis' });
          }

          // Add Blind Spot Acknowledgement subsection
          if ((data as any).blindSpotAcknowledgement) {
            sections.push({ id: 'blind-spot-acknowledgement', title: '  Limitations & Blind Spots' });
          }

          // Add Professional Disclaimer subsection
          if ((data as any).professionalDisclaimer) {
            sections.push({ id: 'professional-disclaimer', title: '  Professional Disclaimer' });
          }
        }

        // Add Financial Analysis specific sections
        const workflowType = data.responseMetadata?.workflowType?.toLowerCase() || '';
        const isFinancialAnalysis = workflowType.includes('financial_analysis') ||
          workflowType.includes('financial analysis') ||
          // Also detect by unique data structure
          ((data as any).financialRiskAssessment && (data as any).ermRiskMapping);
        if (isFinancialAnalysis) {
          // Financial Risk Assessment
          if ((data as any).financialRiskAssessment) {
            sections.push({ id: 'financial-risk-assessment', title: 'Financial Risk Assessment' });
          }

          // Detailed Analysis
          if ((data as any).detailedAnalysis) {
            sections.push({ id: 'detailed-analysis', title: 'Detailed Analysis' });
            const detailedAnalysis = (data as any).detailedAnalysis;
            if (detailedAnalysis.revenueAnalysis) {
              sections.push({ id: 'revenue-analysis', title: '  Revenue Analysis', isChild: true });
            }
            if (detailedAnalysis.profitabilityAnalysis) {
              sections.push({ id: 'profitability-analysis', title: '  Profitability Analysis', isChild: true });
            }
            if (detailedAnalysis.liquidityAnalysis) {
              sections.push({ id: 'liquidity-analysis', title: '  Liquidity Analysis', isChild: true });
            }
            if (detailedAnalysis.leverageAnalysis) {
              sections.push({ id: 'leverage-analysis', title: '  Leverage Analysis', isChild: true });
            }
            if (detailedAnalysis.cashFlowAnalysis) {
              sections.push({ id: 'cash-flow-analysis', title: '  Cash Flow Analysis', isChild: true });
            }
            if (detailedAnalysis.industrySpecificAnalysis) {
              sections.push({ id: 'industry-specific-analysis', title: '  Industry-Specific Analysis', isChild: true });
            }
            if (detailedAnalysis.footnoteAnalysis) {
              sections.push({ id: 'footnote-analysis', title: '  Footnote Analysis', isChild: true });
            }
          }

          // Period Comparison
          if ((data as any).periodComparison) {
            sections.push({ id: 'period-comparison', title: 'Period Comparison' });
          }

          // ERM Risk Mapping
          if ((data as any).ermRiskMapping) {
            sections.push({ id: 'erm-risk-mapping', title: 'ERM Risk Mapping' });
            const ermRiskMapping = (data as any).ermRiskMapping;
            if (ermRiskMapping.strategicRisks && ermRiskMapping.strategicRisks.length > 0) {
              sections.push({ id: 'strategic-risks', title: '  Strategic Risks', isChild: true });
            }
            if (ermRiskMapping.operationalRisks && ermRiskMapping.operationalRisks.length > 0) {
              sections.push({ id: 'operational-risks', title: '  Operational Risks', isChild: true });
            }
            if (ermRiskMapping.financialRisks && ermRiskMapping.financialRisks.length > 0) {
              sections.push({ id: 'financial-risks', title: '  Financial Risks', isChild: true });
            }
            if (ermRiskMapping.complianceRisks && ermRiskMapping.complianceRisks.length > 0) {
              sections.push({ id: 'compliance-risks', title: '  Compliance Risks', isChild: true });
            }
          }

          // Underwriting Recommendation
          if ((data as any).underwritingRecommendation) {
            sections.push({ id: 'underwriting-recommendation', title: 'Underwriting Recommendation' });
          }

          // Confidence and Limitations
          if ((data as any).confidenceAndLimitations) {
            sections.push({ id: 'financial-confidence-limitations', title: 'Confidence & Limitations' });
          }

          // Professional Disclaimer
          if ((data as any).professionalDisclaimer) {
            sections.push({ id: 'financial-professional-disclaimer', title: 'Professional Disclaimer' });
          }
        }

        // Add Coverage Analysis specific sections
        if ((data as any).policyOverview) {
          sections.push({ id: 'policy-overview', title: 'Policy Overview' });
        }
        if ((data as any).coverageAnalysisMatrices && Array.isArray((data as any).coverageAnalysisMatrices) && (data as any).coverageAnalysisMatrices.length > 0) {
          sections.push({ id: 'coverage-analysis-matrices', title: 'Coverage Analysis Matrices' });
        }
        if ((data as any).coverageRecommendations) {
          sections.push({ id: 'coverage-recommendations', title: 'Coverage Recommendations' });
        }
        if ((data as any).coverageGapsAndOpportunities) {
          sections.push({ id: 'coverage-gaps-and-opportunities', title: 'Coverage Gaps and Opportunities' });
        }
        if ((data as any).scenarioTesting && Array.isArray((data as any).scenarioTesting) && (data as any).scenarioTesting.length > 0) {
          sections.push({ id: 'scenario-testing', title: 'Scenario Testing' });
        }
        if ((data as any).coverageMetrics) {
          sections.push({ id: 'coverage-metrics', title: 'Coverage Metrics' });
        }
        if ((data as any).analysisSummary) {
          sections.push({ id: 'analysis-summary', title: 'Analysis Summary' });
        }
        if ((data as any).coverageSummary) {
          sections.push({ id: 'coverage-summary', title: 'Coverage Summary' });
        }

        // Add YOY Renewal specific sections
        if ((data as any).strategicActionPlan) {
          sections.push({ id: 'strategic-action-plan', title: 'Strategic Action Plan' });
        }
        if ((data as any).confidenceStatement) {
          sections.push({ id: 'confidence-statement', title: 'Confidence Statement' });
        }
        // YOY Renewal opt-in add-on sections (Report Options step in modal).
        // Produced by a separate focused AI call (the base schema is at
        // Vertex AI's structured-output complexity ceiling) and merged into
        // the saved report under `reportAddOns`.
        //
        // Tolerate every shape Gemini has produced for `schedules`:
        //   - bare array  [ {…}, {…} ]                      (canonical)
        //   - wrapped     { schedules: [ {…}, {…} ] }       (double-wrap bug from old schema)
        //   - over-nested [ [ {…}, {…} ] ]                  (extra-array wrap)
        const rawSchedules: any = (data as any).reportAddOns?.schedules;
        let scheduleCount = 0;
        if (Array.isArray(rawSchedules)) {
          scheduleCount = rawSchedules.length > 0 && rawSchedules.every((s: any) => Array.isArray(s))
            ? rawSchedules.flat().length
            : rawSchedules.length;
        } else if (rawSchedules && Array.isArray(rawSchedules.schedules)) {
          scheduleCount = rawSchedules.schedules.length;
        }
        if (scheduleCount > 0) {
          sections.push({ id: 'schedules-section', title: 'Schedules' });
        }
        if ((data as any).reportAddOns?.coverageDetails) {
          sections.push({ id: 'coverage-details-section', title: 'Coverage Details' });
        }
        if ((data as any).reportAddOns?.complianceContractual) {
          sections.push({ id: 'compliance-contractual-section', title: 'Compliance & Contractual' });
        }
        if ((data as any).disclaimer) {
          sections.push({ id: 'disclaimer', title: 'Disclaimer' });
        }

        // Add Client Renewal Presentation specific sections
        const isClientRenewalPresentation = data.responseMetadata?.workflowType === 'client_renewal_presentation' ||
          ((data as any).coverageHighlights && (data as any).restrictiveEndorsements);
        if (isClientRenewalPresentation) {
          // Company Context
          if ((data as any).companyContext) {
            sections.push({ id: 'company-context', title: 'Company Context' });
          }
          // Document Inventory
          if ((data as any).documentInventory && (data as any).documentInventory.length > 0) {
            sections.push({ id: 'document-inventory', title: 'Document Inventory' });
          }
          // Program Overview
          if ((data as any).programOverview) {
            sections.push({ id: 'program-overview', title: 'Program Overview' });
          }
          // Coverage Highlights
          if ((data as any).coverageHighlights && (data as any).coverageHighlights.length > 0) {
            sections.push({ id: 'coverage-highlights', title: 'Coverage Highlights' });
          }
          // Restrictive Endorsements
          if ((data as any).restrictiveEndorsements) {
            sections.push({ id: 'restrictive-endorsements', title: 'Restrictive Endorsements' });
            // Add subsections
            if ((data as any).restrictiveEndorsements.criticalRestrictions?.length > 0) {
              sections.push({ id: 'critical-restrictions', title: '  Critical Restrictions', isChild: true });
            }
            if ((data as any).restrictiveEndorsements.importantRestrictions?.length > 0) {
              sections.push({ id: 'important-restrictions', title: '  Important Restrictions', isChild: true });
            }
            if ((data as any).restrictiveEndorsements.positiveEndorsements?.length > 0) {
              sections.push({ id: 'positive-endorsements', title: '  Positive Endorsements', isChild: true });
            }
          }
          // Premium Analysis
          if ((data as any).premiumAnalysis) {
            sections.push({ id: 'premium-analysis', title: 'Premium Analysis' });
          }
          // Coverage Changes
          if ((data as any).coverageChanges && (data as any).coverageChanges.length > 0) {
            sections.push({ id: 'coverage-changes', title: 'Coverage Changes' });
          }
          // Red Flags
          if ((data as any).redFlags && (data as any).redFlags.length > 0) {
            sections.push({ id: 'red-flags', title: 'Red Flags' });
          }
          // Positive Signals
          if ((data as any).positiveSignals && (data as any).positiveSignals.length > 0) {
            sections.push({ id: 'positive-signals', title: 'Positive Signals' });
          }
          // Action Items
          if ((data as any).actionItems && (data as any).actionItems.length > 0) {
            sections.push({ id: 'action-items', title: 'Action Items' });
          }
          // Conversation Points
          if ((data as any).conversationPoints && (data as any).conversationPoints.length > 0) {
            sections.push({ id: 'conversation-points', title: 'Conversation Points' });
          }
          // Glossary
          if ((data as any).glossary && (data as any).glossary.length > 0) {
            sections.push({ id: 'glossary', title: 'Glossary' });
          }
          // Confidence & Limitations
          if ((data as any).confidenceAndLimitations) {
            sections.push({ id: 'confidence-limitations', title: 'Confidence & Limitations' });
          }
          // Professional Disclaimer
          if ((data as any).professionalDisclaimer) {
            sections.push({ id: 'professional-disclaimer', title: 'Professional Disclaimer' });
          }
        }

        // Add Homeowners Comparison / Personal Umbrella specific sections
        const isHomeownersOrUmbrella = data.responseMetadata?.workflowType === 'homeowners_comparison' ||
          data.responseMetadata?.workflowType === 'personal_umbrella' ||
          ((data as any).baselineExtraction && (data as any).gapSynthesis && (data as any).documentClassification);
        if (isHomeownersOrUmbrella) {
          if ((data as any).analysisHeader) {
            sections.push({ id: 'analysis-header', title: 'Analysis Header' });
          }
          if ((data as any).modeRationale) {
            sections.push({ id: 'mode-rationale', title: 'Mode Rationale' });
          }
          if ((data as any).documentClassification) {
            sections.push({ id: 'document-classification', title: 'Document Classification' });
          }
          if ((data as any).temporalVerification) {
            sections.push({ id: 'temporal-verification', title: 'Temporal Verification' });
          }
          if ((data as any).policyArchitecture) {
            sections.push({ id: 'policy-architecture', title: 'Policy Architecture' });
          }
          if ((data as any).scheduleOfForms) {
            sections.push({ id: 'schedule-of-forms', title: 'Schedule of Forms' });
          }
          if ((data as any).baselineExtraction) {
            sections.push({ id: 'baseline-extraction', title: 'Baseline Coverage Extraction' });
          }
          if ((data as any).exposureProfile) {
            sections.push({ id: 'exposure-profile', title: 'Exposure Profile' });
          }
          if ((data as any).dropDownMatrix) {
            sections.push({ id: 'drop-down-matrix', title: 'Drop-Down Coverage Matrix' });
          }
          if ((data as any).coverageAnalysis) {
            sections.push({ id: 'coverage-analysis-ho', title: 'Coverage Analysis' });
          }
          if ((data as any).compoundFailures) {
            sections.push({ id: 'compound-failures', title: 'Compound Failures' });
          }
          if ((data as any).appearanceOfCoverage) {
            sections.push({ id: 'appearance-of-coverage', title: 'Appearance of Coverage' });
          }
          if ((data as any).premiumAnalysis) {
            sections.push({ id: 'premium-analysis-ho', title: 'Premium Analysis' });
          }
          if ((data as any).gapSynthesis) {
            sections.push({ id: 'gap-synthesis', title: 'Gap Synthesis' });
          }
          if ((data as any).decisionFramework) {
            sections.push({ id: 'decision-framework', title: 'Decision Framework' });
          }
          if ((data as any).disclaimer) {
            sections.push({ id: 'disclaimer', title: 'Disclaimer' });
          }
        }

        // BETA suite sections (cyber, D&O, Home, Premium Dev, Loss Run, RE) —
        // mirrors the ids SuiteReportRenderer actually renders (see idPrefix
        // there); one entry per base report section, plus one per
        // narrativeSections item and per merged enhancement so the TOC stays
        // accurate as sections come and go.
        const suiteWorkflowType = data.responseMetadata?.workflowType || '';
        if (isSuiteWorkflowType(suiteWorkflowType)) {
          const pushCyberSections = (prefix: string, sectionData: any, label?: string) => {
            sections.push({ id: `${prefix}-header`, title: label || 'Substrate & Routing' });
            if (sectionData?.inputsInventory) {
              sections.push({ id: `${prefix}-inputs`, title: '  Inputs', isChild: !!label });
            }
            if (sectionData?.executiveSummary) {
              sections.push({ id: `${prefix}-executive-summary`, title: '  Executive Summary', isChild: !!label });
            }
            if (sectionData?.ledger && sectionData.ledger.length > 0) {
              sections.push({ id: `${prefix}-ledger`, title: '  Ledger', isChild: !!label });
            }
            (sectionData?.narrativeSections || []).forEach((ns: any) => {
              sections.push({ id: `${prefix}-narrative-${ns.id}`, title: `  ${ns.title}`, isChild: true });
            });
            if (sectionData?.researchScope && sectionData.researchScope.length > 0) {
              sections.push({ id: `${prefix}-research-scope`, title: '  Research Scope', isChild: !!label });
            }
            if (sectionData?.reconciliation) {
              sections.push({ id: `${prefix}-reconciliation`, title: '  Reconciliation', isChild: !!label });
            }
          };

          pushCyberSections('suite', data);
          // Order matches the renderer: base -> merged enhancements -> the
          // "Suggested Analyses" prompt block, which always sits last (even
          // on an already-merged report).
          Object.entries(data.enhancements || {}).forEach(([toolType, enhancementData]: [string, any]) => {
            const enhancementTitle = (enhancementData?.responseMetadata?.toolVersion || toolType).replace(/ v0\.\d+$/, '');
            pushCyberSections(`suite-enh-${toolType}`, enhancementData, enhancementTitle);
          });
          // Dedupe suggestedAnalyses by toolType for the count check below —
          // mirrors the renderer's defensive dedupe so the TOC entry only
          // appears when there's actually something left to show.
          const dedupedSuggestionCount = new Set(
            (data.suggestedAnalyses || []).map((s: any) => s?.toolType).filter(Boolean)
          ).size;
          if (dedupedSuggestionCount > 0) {
            sections.push({ id: 'suite-suggested-analyses', title: 'Suggested Analyses' });
          }
        }
      }
      // Handle Markdown format - extract specific known sections
      else if (typeof workflowData.content === 'string') {
        const content = workflowData.content;

        // Define common section patterns
        const sectionPatterns = [
          'Key Findings',
          'Executive Summary',
          'Summary',
          'Recommendations',
          'Strategic Recommendations',
          'Risk Assessment',
          'Risk Analysis',
          'Coverage Analysis',
          'Premium Analysis',
          'Conclusion',
          'Named Insured Profile',
          'Market Research',
          'Comparative Analysis',
          'Loss Run Analysis'
        ];

        sectionPatterns.forEach(pattern => {
          // Look for this section in the markdown content (case-insensitive)
          const regex = new RegExp(`^#+\\s*${pattern}\\s*$`, 'mi');
          if (regex.test(content)) {
            sections.push({
              id: createSectionId(pattern),
              title: pattern
            });
          }
        });
      }
    } catch (error) {
      console.error('Error extracting report sections:', error);
    }

    // Append the Sources footer entry (always emitted by UniversalJsonRenderer
    // when responseMetadata.documentsAnalyzed/documentsProcessed exists).
    // The phantom-section filter will drop it on reports that don't render
    // one, so it's safe to push unconditionally.
    if (!sections.some((s) => s.id === 'sources')) {
      sections.push({ id: 'sources', title: 'Sources' });
    }

    return sections;
  }, [workflowData]);

  // Function to scroll to a section
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSectionId(sectionId);
    } else {
      console.log('🔍 Scroll debug - element not found:', sectionId);
    }
  };

  // Show a sticky mini-header once the user has scrolled past the report's
  // primary header bar (~200px down). Use rAF to avoid scroll-spam re-renders.
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setShowStickyHeader(window.scrollY > 200);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // After mount + each data change, measure which reportSections have a
  // matching DOM element and drop the ghosts. We measure twice (immediately
  // and a beat later) because some sections are added by async render paths
  // inside UniversalJsonRenderer that haven't committed on the first frame.
  useEffect(() => {
    if (!reportSections || reportSections.length === 0) {
      setValidSectionIds(null);
      return;
    }
    const measure = () => {
      const found = new Set<string>();
      reportSections.forEach((s) => {
        if (document.getElementById(s.id)) found.add(s.id);
      });
      setValidSectionIds(found);
    };
    measure();
    const t1 = setTimeout(measure, 600);
    const t2 = setTimeout(measure, 2000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [reportSections]);

  // Track which section is currently in view to highlight TOC entry
  useEffect(() => {
    if (!reportSections || reportSections.length === 0) return;

    const elements: HTMLElement[] = [];
    reportSections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) elements.push(el);
    });
    if (elements.length === 0) return;

    const visibilityMap = new Map<string, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visibilityMap.set(entry.target.id, entry.intersectionRatio);
        });
        let bestId: string | null = null;
        let bestRatio = 0;
        visibilityMap.forEach((ratio, id) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        });
        if (bestId && bestRatio > 0) setActiveSectionId(bestId);
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: [0, 0.1, 0.25, 0.5, 1] }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [reportSections]);

  // Function to parse quote-based citations  
  const parseQuoteCitations = (text: string) => {
    const citations: any[] = [];
    const quoteCitationRegex = /\[CITE:\s*"([^"]+)"\]/g;
    let match;
    let index = 0;
    
    while ((match = quoteCitationRegex.exec(text)) !== null) {
      const quote = match[1];
      citations.push({
        id: `cite-${index++}`,
        type: 'quote',
        text: quote,
        displayText: quote.length > 50 ? quote.substring(0, 47) + '...' : quote,
        fullMatch: match[0],
        startIndex: match.index,
        endIndex: match.index + match[0].length
      });
    }
    
    return citations;
  };

  // Function to render content with citations converted to clickable elements
  const renderContentWithCitations = (text: string): React.ReactNode => {
    console.log('🔍 Attempting to render content:', {
      contentLength: text?.length || 0,
      contentType: typeof text,
      isResearchWorkflow: workflowData?.workflowType === 'research_tool',
      contentPreview: text?.substring(0, 100)
    });
    
    // First, try to parse as JSON
    try {
      let jsonString = text.trim();

      // Strip markdown code fences if present (common from AI responses)
      if (jsonString.startsWith('```json')) {
        jsonString = jsonString.replace(/^```json\s*\n?/, '').replace(/\n?```\s*$/, '');
      } else if (jsonString.startsWith('```')) {
        jsonString = jsonString.replace(/^```\s*\n?/, '').replace(/\n?```\s*$/, '');
      }

      // Look for JSON object boundaries
      const jsonStart = jsonString.indexOf('{');
      const jsonEnd = jsonString.lastIndexOf('}');

      if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
        jsonString = jsonString.substring(jsonStart, jsonEnd + 1);
      }

      // Fix trailing commas in JSON (common AI response issue)
      // Remove trailing commas before ] or }
      jsonString = jsonString.replace(/,(\s*[\]}])/g, '$1');

      let jsonContent = JSON.parse(jsonString);
      
      // Handle double-encoded JSON from legacy coverage analysis reports
      if (typeof jsonContent === 'string') {
        console.log('🔧 Detected double-encoded JSON string, attempting to parse again...');
        try {
          jsonContent = JSON.parse(jsonContent);
          console.log('✅ Successfully parsed double-encoded JSON');
        } catch (doubleParseError) {
          console.warn('⚠️ Failed to parse double-encoded JSON:', doubleParseError);
          // Keep the original string content for fallback
        }
      }
      
      if (jsonContent && typeof jsonContent === 'object') {
        console.log('🎨 WorkflowReport: Parsed JSON content, checking structure...');
        console.log('🔍 Full JSON structure:', jsonContent);
        console.log('🔍 Available keys:', Object.keys(jsonContent));

        // BETA suite: one dedicated renderer for the shared envelope,
        // detected here rather than bolted into UniversalJsonRenderer.
        const suiteWorkflowType = jsonContent.responseMetadata?.workflowType || workflowData?.workflowType;
        if (isSuiteWorkflowType(suiteWorkflowType)) {
          console.log('🎯 BETA suite report detected - using SuiteReportRenderer');
          return (
            <SuiteReportRenderer
              data={jsonContent}
              onCitationClick={handleUniversalCitationClick}
              onExportPdf={handleExportPDF}
              hideCitations={hideCitations}
              customerName={workflowData?.customerName}
              reportId={workflowData?.reportId}
              reportTitle={workflowData?.reportTitle}
            />
          );
        }

        // Validate that the JSON has the required structure for UniversalJsonRenderer
        // Accept native format, year-over-year format, loss run analysis format, renewal analysis format, and insurance requirements format
        const hasRequiredStructure = (
          jsonContent.responseMetadata &&
          jsonContent.executiveSummary &&
          (
            // Policy comparison format
            (jsonContent.analysisMatrices || jsonContent.criticalAlerts) &&
            (jsonContent.keyFindings || jsonContent.namedInsuredProfile)
          ) ||
          (
            // Coverage analysis format - check multiple sources for workflowType
            (jsonContent.responseMetadata?.workflowType === 'coverage_analysis' ||
             workflowData?.workflowType === 'coverage_analysis') &&
            (jsonContent.keyFindings || jsonContent.executiveSummary || jsonContent.coverageGaps ||
             jsonContent.coverageAnalysisMatrices || jsonContent.coverageRecommendations ||
             jsonContent.coverageMetrics || jsonContent.policyOverview)
          ) ||
          (
            // Loss run analysis format (original)
            jsonContent.analysisMatrices &&
            jsonContent.keyFindings &&
            (jsonContent.riskManagementMatrix || jsonContent.trendAnalysis)
          ) ||
          (
            // Loss run forensic analysis format (new structure)
            jsonContent.responseMetadata?.workflowType === 'loss_run_analysis' &&
            (jsonContent.categoryBreakdown || jsonContent.severityBreakdown ||
             jsonContent.riskHeatMap || jsonContent.detailedClaimsList || jsonContent.lossRunSummary)
          ) ||
          (
            // Loss run forensic format without responseMetadata check
            (jsonContent.categoryBreakdown || jsonContent.severityBreakdown) &&
            (jsonContent.riskHeatMap || jsonContent.detailedClaimsList)
          ) ||
          (
            // Renewal analysis format (year-over-year)
            jsonContent.analysisMatrices &&
            jsonContent.recommendations &&
            (jsonContent.clauseWordingComparison || jsonContent.responseMetadata?.workflowType === 'renewal_analysis')
          ) ||
          (
            // Insurance requirements format
            jsonContent.responseMetadata?.workflowType === 'insurance_requirements' &&
            jsonContent.complianceTable &&
            jsonContent.tldr
          ) ||
          (
            // Portfolio analyzer format - with workflowType
            (jsonContent.responseMetadata?.workflowType === 'portfolio_analyzer' ||
             workflowData?.workflowType === 'portfolio_analyzer') &&
            (jsonContent.portfolioStructure || jsonContent.portfolioSynthesis ||
             jsonContent.scenarioAnalysis || jsonContent.coverageAnalysis)
          ) ||
          (
            // Portfolio analyzer format - detected by structure alone (no workflowType required)
            jsonContent.portfolioStructure && jsonContent.portfolioSynthesis
          ) ||
          (
            // Homeowners comparison / Personal umbrella format
            (jsonContent.responseMetadata?.workflowType === 'homeowners_comparison' ||
             jsonContent.responseMetadata?.workflowType === 'personal_umbrella' ||
             workflowData?.workflowType === 'homeowners_comparison' ||
             workflowData?.workflowType === 'personal_umbrella') &&
            (jsonContent.documentClassification || jsonContent.baselineExtraction ||
             jsonContent.gapSynthesis || jsonContent.coverageAnalysis)
          )
        );
        
        console.log('🔍 Structure validation:', {
          hasResponseMetadata: !!jsonContent.responseMetadata,
          hasExecutiveSummary: !!jsonContent.executiveSummary,
          hasAnalysisMatrices: !!jsonContent.analysisMatrices,
          hasCoverageAnalysisMatrices: !!jsonContent.coverageAnalysisMatrices,
          hasCriticalAlerts: !!jsonContent.criticalAlerts,
          hasKeyFindings: !!jsonContent.keyFindings,
          hasNamedInsuredProfile: !!jsonContent.namedInsuredProfile,
          hasRiskManagementMatrix: !!jsonContent.riskManagementMatrix,
          hasTrendAnalysis: !!jsonContent.trendAnalysis,
          hasRecommendations: !!jsonContent.recommendations,
          hasClauseWordingComparison: !!jsonContent.clauseWordingComparison,
          hasComplianceTable: !!jsonContent.complianceTable,
          hasTldr: !!jsonContent.tldr,
          // Portfolio analyzer fields
          hasPortfolioStructure: !!jsonContent.portfolioStructure,
          hasPortfolioSynthesis: !!jsonContent.portfolioSynthesis,
          hasScenarioAnalysis: !!jsonContent.scenarioAnalysis,
          hasRequiredStructure,
          workflowType: jsonContent.responseMetadata?.workflowType,
          passedWorkflowType: workflowData?.workflowType
        });
        
        if (hasRequiredStructure) {
          console.log('✅ WorkflowReport: JSON has valid UniversalJsonRenderer structure');
          console.log('🎯 Passing to UniversalJsonRenderer with data:', {
            workflowType: jsonContent.responseMetadata?.workflowType,
            sectionsAvailable: Object.keys(jsonContent).filter(key => key !== 'responseMetadata')
          });
          
          try {
            // Check if this is a loss run analysis (original or forensic format)
            const isLossRunAnalysis = jsonContent.responseMetadata?.workflowType === 'loss_run_analysis' ||
              (jsonContent.categoryBreakdown || jsonContent.severityBreakdown) &&
              (jsonContent.riskHeatMap || jsonContent.detailedClaimsList);

            if (isLossRunAnalysis) {
              console.log('🎯 Loss run analysis detected - passing directly to UniversalJsonRenderer');
              console.log('🎯 Loss run data for debugging:', {
                workflowType: jsonContent.responseMetadata?.workflowType,
                analysisMatricesCount: jsonContent.analysisMatrices?.length,
                hasCategoryBreakdown: !!jsonContent.categoryBreakdown,
                hasSeverityBreakdown: !!jsonContent.severityBreakdown,
                hasRiskHeatMap: !!jsonContent.riskHeatMap,
                hasDetailedClaimsList: !!jsonContent.detailedClaimsList,
                detailedClaimsCount: jsonContent.detailedClaimsList?.length
              });
              return (
                <UniversalJsonRenderer
                  data={jsonContent}
                  onCitationClick={handleUniversalCitationClick}
                  hideCitations={hideCitations}
                  customerName={workflowData?.customerName}
                  documents={workflowData?.documents || []}
                  customPdfExportHandler={handleExportPDF}
                  workflowType={workflowData?.workflowType}
                  compactHeader={true}
                  exportPortalRef={exportSlotRef}
                />
              );
            }

            // Check if this is a coverage analysis (pass directly to UniversalJsonRenderer)
            if (jsonContent.responseMetadata?.workflowType === 'coverage_analysis' || workflowData?.workflowType === 'coverage_analysis') {
              console.log('🎯 Coverage analysis detected - passing directly to UniversalJsonRenderer');
              console.log('🔍 DEBUG: Coverage analysis workflowType match successful');
              console.log('🎯 Coverage analysis data for debugging:', {
                analysisMatricesCount: jsonContent.analysisMatrices?.length,
                coverageAnalysisMatricesCount: jsonContent.coverageAnalysisMatrices?.length,
                keyFindingsCount: jsonContent.keyFindings?.findings?.length,
                executiveSummaryParagraphs: jsonContent.executiveSummary?.paragraphs?.length
              });
              return (
                <UniversalJsonRenderer
                  data={jsonContent}
                  onCitationClick={handleUniversalCitationClick}
                  hideCitations={hideCitations}
                  customerName={workflowData?.customerName}
                  documents={workflowData?.documents || []}
                  customPdfExportHandler={handleExportPDF}
                  workflowType={workflowData?.workflowType}
                  compactHeader={true}
                  exportPortalRef={exportSlotRef}
                />
              );
            }

            // Check if this is insurance requirements analysis (pass directly to UniversalJsonRenderer)
            if (jsonContent.responseMetadata?.workflowType === 'insurance_requirements') {
              console.log('🎯 Insurance requirements analysis detected - passing directly to UniversalJsonRenderer');
              console.log('🎯 Insurance requirements data for debugging:', {
                hasComplianceTable: !!jsonContent.complianceTable,
                complianceTableCount: jsonContent.complianceTable?.length,
                hasTldr: !!jsonContent.tldr,
                hasExecutiveSummary: !!jsonContent.executiveSummary,
                hasBlindSpotAcknowledgement: !!jsonContent.blindSpotAcknowledgement
              });
              return (
                <UniversalJsonRenderer
                  data={jsonContent}
                  onCitationClick={handleUniversalCitationClick}
                  hideCitations={hideCitations}
                  customerName={workflowData?.customerName}
                  documents={workflowData?.documents || []}
                  customPdfExportHandler={handleExportPDF}
                  workflowType={workflowData?.workflowType}
                  compactHeader={true}
                  exportPortalRef={exportSlotRef}
                />
              );
            }

            // Check if this is portfolio analyzer (pass directly to UniversalJsonRenderer)
            // Detect by workflowType OR by characteristic fields (portfolioStructure, portfolioSynthesis)
            const isPortfolioAnalyzer =
              jsonContent.responseMetadata?.workflowType === 'portfolio_analyzer' ||
              workflowData?.workflowType === 'portfolio_analyzer' ||
              (jsonContent.portfolioStructure && jsonContent.portfolioSynthesis);
            if (isPortfolioAnalyzer) {
              console.log('🎯 Portfolio analyzer detected - passing directly to UniversalJsonRenderer');
              console.log('🎯 Portfolio analyzer data for debugging:', {
                hasPortfolioStructure: !!jsonContent.portfolioStructure,
                hasPortfolioSynthesis: !!jsonContent.portfolioSynthesis,
                hasScenarioAnalysis: !!jsonContent.scenarioAnalysis,
                hasCoverageAnalysis: !!jsonContent.coverageAnalysis,
                hasAssessment: !!jsonContent.assessment,
                hasRecommendations: !!jsonContent.recommendations
              });
              return (
                <UniversalJsonRenderer
                  data={jsonContent}
                  onCitationClick={handleUniversalCitationClick}
                  hideCitations={hideCitations}
                  customerName={workflowData?.customerName}
                  documents={workflowData?.documents || []}
                  customPdfExportHandler={handleExportPDF}
                  workflowType={workflowData?.workflowType}
                  compactHeader={true}
                  exportPortalRef={exportSlotRef}
                />
              );
            }

            // Check if this is homeowners comparison or personal umbrella (pass directly to UniversalJsonRenderer)
            const isHomeownersOrUmbrella =
              jsonContent.responseMetadata?.workflowType === 'homeowners_comparison' ||
              jsonContent.responseMetadata?.workflowType === 'personal_umbrella' ||
              workflowData?.workflowType === 'homeowners_comparison' ||
              workflowData?.workflowType === 'personal_umbrella';
            if (isHomeownersOrUmbrella) {
              console.log('🎯 Homeowners/Umbrella detected - passing directly to UniversalJsonRenderer');
              return (
                <UniversalJsonRenderer
                  data={jsonContent}
                  onCitationClick={handleUniversalCitationClick}
                  hideCitations={hideCitations}
                  customerName={workflowData?.customerName}
                  documents={workflowData?.documents || []}
                  customPdfExportHandler={handleExportPDF}
                  workflowType={workflowData?.workflowType}
                  compactHeader={true}
                  exportPortalRef={exportSlotRef}
                />
              );
            }

            // Convert year-over-year structure to UniversalJsonRenderer format
            if (jsonContent.criticalAlerts && !jsonContent.analysisMatrices) {
              console.log('🔄 Converting criticalAlerts to analysisMatrices in renderContentWithCitations');
              jsonContent.analysisMatrices = [];
              
              // Convert each critical alert category to a matrix
              Object.keys(jsonContent.criticalAlerts).forEach(alertType => {
                const alerts = jsonContent.criticalAlerts[alertType];
                console.log(`Processing alert type: ${alertType}`, alerts);
                
                if (Array.isArray(alerts) && alerts.length > 0) {
                  const title = alertType.replace(/([A-Z])/g, ' $1')
                                           .replace(/^./, str => str.toUpperCase())
                                           .replace('priority1_', 'Priority 1: ')
                                           .replace('priority2_', 'Priority 2: ')
                                           .replace('priority3_', 'Priority 3: ')
                                           .replace('priority4_', 'Priority 4: ')
                                           .replace(/([a-z])([A-Z])/g, '$1 $2');
                  
                  // Create matrix with appropriate headers based on alert type
                  let headers = ['Item', 'Description', 'Impact'];
                  if (alertType.includes('premium') || alertType.includes('cost')) {
                    headers = ['Change Type', 'Description', 'Previous', 'Current', 'Impact'];
                  } else if (alertType.includes('coverage') || alertType.includes('limit')) {
                    headers = ['Coverage', 'Change Type', 'Previous Limit', 'Current Limit', 'Impact'];
                  } else if (alertType.includes('exclusion') || alertType.includes('gap')) {
                    headers = ['Type', 'Form/Title', 'Description', 'Impact'];
                  } else if (alertType.includes('terms') || alertType.includes('condition')) {
                    headers = ['Change Type', 'Description', 'Previous Terms', 'Current Terms', 'Impact'];
                  }
                  
                  const rows = alerts.map(alert => {
                    const rowData: any = {};
                    headers.forEach((header, index) => {
                      switch(header.toLowerCase().replace(' ', '')) {
                        case 'item':
                        case 'changetype':
                        case 'type':
                          rowData[`col_${index}`] = alert.changeType || alert.exclusionType || alert.coveragePart || 'Change';
                          break;
                        case 'description':
                          rowData[`col_${index}`] = alert.description || alert.title || '';
                          break;
                        case 'previous':
                        case 'previouslimit':
                        case 'previousterms':
                          rowData[`col_${index}`] = alert.expiringValue || alert.expiringLimit || alert.expiringTerms || '';
                          break;
                        case 'current':
                        case 'currentlimit':
                        case 'currentterms':
                          rowData[`col_${index}`] = alert.renewalValue || alert.renewalLimit || alert.renewalTerms || '';
                          break;
                        case 'form/title':
                          rowData[`col_${index}`] = alert.formNumber || alert.title || '';
                          break;
                        case 'coverage':
                          rowData[`col_${index}`] = alert.coveragePart || '';
                          break;
                        case 'impact':
                          rowData[`col_${index}`] = alert.impact || alert.riskImplication || alert.riskImpact || '';
                          break;
                        default:
                          rowData[`col_${index}`] = '';
                      }
                    });
                    return rowData;
                  });
                  
                  jsonContent.analysisMatrices.push({
                    title: title,
                    headers: headers,
                    rows: rows
                  });
                }
              });
              
              console.log(`✅ Created ${jsonContent.analysisMatrices.length} analysis matrices`);
            }
            
            if (jsonContent.namedInsuredProfile && !jsonContent.keyFindings) {
              console.log('🔄 Converting namedInsuredProfile to keyFindings in renderContentWithCitations');
              const findings: any[] = [];
              
              // Extract findings from namedInsuredProfile changes
              if (jsonContent.namedInsuredProfile.changes) {
                Object.keys(jsonContent.namedInsuredProfile.changes).forEach(changeType => {
                  const change = jsonContent.namedInsuredProfile.changes[changeType];
                  if (change !== 'No' && change !== false && change !== 'No change') {
                    findings.push({
                      finding: {
                        text: `${changeType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}: ${change}`,
                        citations: []
                      },
                      implication: {
                        text: 'Review the impact of this change on coverage and risk profile',
                        citations: []
                      }
                    });
                  }
                });
              }
              
              // Extract additional findings from actionableRecommendations if available
              if (jsonContent.actionableRecommendations && Array.isArray(jsonContent.actionableRecommendations)) {
                jsonContent.actionableRecommendations.slice(0, 3).forEach((rec: any) => {
                  if (rec.finding && rec.finding.text) {
                    findings.push({
                      finding: {
                        text: rec.finding.text,
                        citations: []
                      },
                      implication: {
                        text: rec.recommendation?.text || rec.rationale?.text || 'Action required',
                        citations: []
                      }
                    });
                  }
                });
              }
              
              // Extract findings from marketContext if available
              if (jsonContent.marketContext && jsonContent.marketContext.hardMarketIndicators) {
                const indicators = jsonContent.marketContext.hardMarketIndicators;
                if (Array.isArray(indicators) && indicators.length > 0) {
                  findings.push({
                    finding: {
                      text: `Market conditions: ${indicators.join(', ')}`,
                      citations: []
                    },
                    implication: {
                      text: jsonContent.marketContext.marketingRecommendation || 'Consider market timing for renewals',
                      citations: []
                    }
                  });
                }
              }
              
              jsonContent.keyFindings = {
                summary: {
                  text: jsonContent.executiveSummary?.paragraphs?.[0]?.content?.text || 'Year-over-year renewal analysis completed',
                  citations: []
                },
                findings: findings
              };
              
              console.log(`✅ Created keyFindings with ${findings.length} findings`);
            }

            return (
              <UniversalJsonRenderer
                data={jsonContent}
                onCitationClick={handleUniversalCitationClick}
                hideCitations={hideCitations}
                customerName={workflowData?.customerName}
                documents={workflowData?.documents || []}
                customPdfExportHandler={handleExportPDF}
                workflowType={workflowData?.workflowType}
                compactHeader={true}
              />
            );
          } catch (renderError) {
            console.error('❌ UniversalJsonRenderer failed:', renderError);
            // Fall through to markdown rendering
          }
        } else {
          console.log('⚠️ WorkflowReport: JSON structure does not match UniversalJsonRenderer interface');
          console.log('Available properties:', Object.keys(jsonContent));
          console.log('Validation details:', {
            hasResponseMetadata: !!jsonContent.responseMetadata,
            hasExecutiveSummary: !!jsonContent.executiveSummary,
            hasAnalysisMatrices: !!jsonContent.analysisMatrices,
            hasKeyFindings: !!jsonContent.keyFindings,
            hasRiskManagementMatrix: !!jsonContent.riskManagementMatrix,
            hasTrendAnalysis: !!jsonContent.trendAnalysis,
            workflowType: jsonContent.responseMetadata?.workflowType,
            policyComparisonCheck: (jsonContent.analysisMatrices || jsonContent.criticalAlerts) && (jsonContent.keyFindings || jsonContent.namedInsuredProfile),
            lossRunAnalysisCheck: jsonContent.analysisMatrices && jsonContent.keyFindings && (jsonContent.riskManagementMatrix || jsonContent.trendAnalysis)
          });
          
          // Render as formatted JSON instead
          return (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                <h3 className="text-yellow-800 font-medium">📊 Structured Analysis Results</h3>
                <p className="text-yellow-600 text-sm mt-1">The report contains structured data but uses a different format.</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <pre className="whitespace-pre-wrap text-sm text-gray-700 overflow-auto max-h-96">
                  {JSON.stringify(jsonContent, null, 2)}
                </pre>
              </div>
            </div>
          );
        }
      }
    } catch (e) {
      console.log('📝 WorkflowReport: Content is not valid JSON, rendering as Markdown');
      console.log('Parse error:', e);
      console.log('Attempting markdown fallback for research workflow:', workflowData?.workflowType === 'research_tool');
      
      // For research workflows, if JSON parsing fails, check if we have any content at all
      if (workflowData?.workflowType === 'research_tool' && (!text || text.trim().length === 0)) {
        return (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
            <h3 className="text-yellow-800 font-medium">⚠️ Research Report Processing</h3>
            <p className="text-yellow-600 text-sm mt-1">
              The research analysis is being processed. Please wait a moment and try reloading the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
            >
              Reload Page
            </button>
          </div>
        );
      }
    }
    
    // Fallback to citation parsing and markdown rendering
    const quoteCitations = parseQuoteCitations(text);
    
    if (quoteCitations.length === 0) {
      // No citations, render normally
      return (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeHighlight, rehypeKatex]}
          components={markdownTableComponents}
        >
          {text}
        </ReactMarkdown>
      );
    }

    // Handle quote-based citations
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    
    quoteCitations.forEach((citation, idx) => {
      // Add text before citation
      if (citation.startIndex > lastIndex) {
        const contentPart = text.substring(lastIndex, citation.startIndex);
        parts.push(
          <ReactMarkdown
            key={`text-${lastIndex}-${citation.startIndex}`}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight, rehypeKatex]}
            components={markdownTableComponents}
          >
            {contentPart}
          </ReactMarkdown>
        );
      }
      
      // Add clickable citation (only if not hidden)
      if (!hideCitations) {
        parts.push(
          <button
            key={citation.id}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline mx-1 px-1 py-0.5 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
            onClick={() => handleCitationClick({ type: 'quote', text: citation.text, found: true })}
            title={`Click to view citation: "${citation.text}"`}
          >
            📑
          </button>
        );
      }
      
      lastIndex = citation.endIndex;
    });
    
    // Add remaining text
    if (lastIndex < text.length) {
      const remainingContent = text.substring(lastIndex);
      parts.push(
        <ReactMarkdown
          key="text-final"
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeHighlight, rehypeKatex]}
          components={markdownTableComponents}
        >
          {remainingContent}
        </ReactMarkdown>
      );
    }
    
    return <div>{parts}</div>;
  };

  // REMOVED ALL REDIRECT LOGIC - NO AUTOMATIC NAVIGATION AWAY FROM PAGE
  const [dataLoadError, setDataLoadError] = useState<string | null>(null);
  
  // Check for data issues but NEVER redirect
  useEffect(() => {
    if (!workflowData || (!workflowData?.content && !workflowData?.universalJsonResponse)) {
      console.warn('📄 No workflow data available yet');
      // Just log - don't redirect or set errors that might cause blank page
    }
  }, [workflowData]);

  // Initialize included documents as empty - user must manually select documents to include
  useEffect(() => {
    if (workflowData?.documents) {
      setIncludedDocuments(new Set());
    }
  }, [workflowData]);

  // Load existing chat session if sessionId provided in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const sessionId = urlParams.get('sessionId');
    
    if (sessionId) {
      loadExistingSession(sessionId);
    }
  }, [location.search]);

  // Load report if reportId provided in URL
  useEffect(() => {
    const loadReportById = async (reportId: string) => {
      try {
        setIsLoadingReportData(true);
        console.log(`📊 Loading report with ID: ${reportId}`);
        const token = localStorage.getItem('riskninja-token');
        const apiUrl = process.env.REACT_APP_API_URL || '';
        
        // Get customerId from URL params or location state for customer reports
        const urlParams = new URLSearchParams(location.search);
        const customerId = urlParams.get('customerId') || (location.state as any)?.customerId;
        
        let response: Response;
        
        // Try customer reports endpoint first (for UnderwritingReports like coverage analysis)
        if (customerId) {
          console.log(`📊 Trying customer reports endpoint for customer ${customerId}`);
          response = await fetch(`${apiUrl}/api/customers/${customerId}/reports/${reportId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
        } else {
          // Skip customer endpoint if no customerId available
          response = new Response(null, { status: 404 });
        }

        // If that fails, try comparison reports endpoint (for ComparisonReports)
        if (!response.ok) {
          console.log('📊 Trying comparison reports endpoint as fallback');
          response = await fetch(`${apiUrl}/api/comparison-reports/${reportId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
        }

        if (!response.ok) {
          throw new Error(`Failed to load report: ${response.statusText}`);
        }

        const report = await response.json();
        console.log('✅ Report loaded:', report);

        // Check if the content is structured JSON for UniversalJsonRenderer
        let isJsonContent = false;
        let jsonContent = null;
        let contentToUse = report.content;
        
        if (typeof report.content === 'string') {
          try {
            jsonContent = JSON.parse(report.content);
            
            // Check if this is structured data for UniversalJsonRenderer
            if (jsonContent && typeof jsonContent === 'object') {
              const hasUniversalStructure =
                jsonContent.responseMetadata ||
                jsonContent.executiveSummary ||
                jsonContent.analysisMatrices ||
                jsonContent.keyFindings ||
                jsonContent.riskManagementMatrix ||
                jsonContent.trendAnalysis ||
                (jsonContent.analysisMatrices || jsonContent.criticalAlerts) ||
                (jsonContent.namedInsuredProfile && jsonContent.actionableRecommendations) ||
                // Loss run forensic analysis structure
                jsonContent.categoryBreakdown ||
                jsonContent.severityBreakdown ||
                jsonContent.riskHeatMap ||
                jsonContent.detailedClaimsList ||
                jsonContent.lossRunSummary ||
                // Portfolio analyzer structure
                jsonContent.portfolioStructure ||
                jsonContent.portfolioSynthesis ||
                jsonContent.scenarioAnalysis ||
                (jsonContent.coverageAnalysis && Array.isArray(jsonContent.coverageAnalysis));

              if (hasUniversalStructure) {
                isJsonContent = true;
                console.log('🎯 Report contains structured JSON suitable for UniversalJsonRenderer');
              }
            }
          } catch (e) {
            console.log('📝 Report content is not valid JSON, will render as text/markdown');
          }
        } else if (typeof report.content === 'object') {
          // Already parsed JSON object
          jsonContent = report.content;
          isJsonContent = true;
          contentToUse = JSON.stringify(report.content, null, 2);
          console.log('🎯 Report content is already a JSON object');
        }

        // Transform the report data to match WorkflowReportData format
        const transformedData: WorkflowReportData = {
          content: contentToUse,
          workflowType: report.primaryPolicyType || report.workflowType || 'coverage_analysis',
          documents: report.documentIds?.map((id: string, index: number) => ({
            id,
            name: report.documentNames?.[index] || `Document ${index + 1}`
          })) || [],
          customerId: report.customerId,
          projectId: report.projectId,
          customerName: report.customerName || '',
          reportTitle: report.title,
          reportId: report.id,
          additionalContext: report.additionalFacts || '',
          // Set Universal JSON flags for proper rendering
          isUniversalJson: isJsonContent,
          universalJsonResponse: isJsonContent ? jsonContent : undefined
        };

        // Update the loaded report data state
        setLoadedReportData(transformedData);

        // Hydrate past reports and pre-check all context items so the user sees everything
        // that went into the original generation as "on" by default in Show What's Sent to AI.
        const loadedPastReports = (report.pastReportIds || []).map((id: string, index: number) => ({
          id,
          title: report.pastReportTitles?.[index] || `Past Report ${index + 1}`
        }));
        setPastReports(loadedPastReports);
        setIncludedPastReports(new Set(loadedPastReports.map((r: { id: string }) => r.id)));
        if (Array.isArray(report.documentIds) && report.documentIds.length > 0) {
          setIncludedDocuments(new Set(report.documentIds));
        }

        setIsLoadingReportData(false);

      } catch (error) {
        console.error('❌ Error loading report:', error);
        setIsLoadingReportData(false);
        alert('Failed to load report. Please try again.');
      }
    };

    const urlParams = new URLSearchParams(location.search);
    const reportId = urlParams.get('reportId') || (location.state as any)?.reportId;

    // DEBUG: Always log the URL params and reportId to see what's happening
    console.log(`🔍 useEffect: reportId loading check:`, {
      currentUrl: location.pathname + location.search,
      reportId,
      hasReportId: !!reportId,
      loadedReportData: !!loadedReportData,
      locationState: location.state,
      hasLocationState: !!location.state,
      locationStateContent: location.state?.content,
      locationStateReportId: (location.state as any)?.reportId,
      allUrlParams: Object.fromEntries(urlParams.entries()),
      conditionCheck: {
        hasReportId: !!reportId,
        noLoadedData: !loadedReportData,
        noLocationContent: (!location.state || !location.state.content),
        shouldLoad: reportId && !loadedReportData && (!location.state || !location.state.content)
      }
    });

    // Only load report if we have a reportId and haven't already loaded report data
    // and we don't have content from location state (allow loading when location.state has session data but no content)
    if (reportId && !loadedReportData && (!location.state || !location.state.content)) {
      console.log(`🔄 Loading report by ID: ${reportId} (no existing content found)`);
      loadReportById(reportId);
    } else if (!reportId) {
      console.warn(`❌ No reportId found in URL or state - cannot load report`, {
        urlReportId: urlParams.get('reportId'),
        stateReportId: (location.state as any)?.reportId,
        allUrlParams: Object.fromEntries(urlParams.entries()),
        locationState: location.state
      });
    } else if (loadedReportData) {
      console.log(`✅ Report already loaded, skipping`);
    } else {
      console.log(`⏭️ Skipping report load due to location.state content`);
    }
  }, [location.search, location.state, loadedReportData]);

  const loadExistingSession = async (sessionId: string) => {
    try {
      setSaveStatus('saving');
      const token = localStorage.getItem('riskninja-token');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${apiUrl}/api/chat/session/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load session');
      }

      const data = await response.json();
      
      if (data.success) {
        setCurrentSessionId(sessionId);
        setSessionTitle(data.session.title || null);

        // Convert messages to chat history format
        const messages = data.messages.map((msg: any, index: number) => ({
          id: `${msg.sender}-${msg.timestamp}-${index}`,
          sender: msg.sender === 'user' ? 'user' : 'ai',
          content: msg.content,
          timestamp: new Date(msg.timestamp),
          citations: [] // Citations will be parsed from content
        }));

        setChatHistory(messages);
        setIsFirstMessage(messages.length === 0);
        setSaveStatus('saved');

        // Set workflowData from session if not already available
        if (!workflowData && data.session) {
          const sessionWorkflowData: WorkflowReportData = {
            projectId: data.session.projectId,
            customerId: data.session.customerId,
            reportId: data.session.reportId,
            workflowType: 'existing_session', // Default for loaded sessions
            documents: [], // Will be populated when report loads
            content: '',
            createdAt: new Date()
          };
          setWorkflowData(sessionWorkflowData);
          console.log('📊 Set workflowData from session data:', sessionWorkflowData);
        }

        console.log(`📂 Loaded existing session ${sessionId} with ${messages.length} messages`);
      }
    } catch (error) {
      console.error('Failed to load session:', error);
      setSaveStatus('error');
    }
  };

  // Context control handlers
  const handleToggleReport = (enabled: boolean) => {
    setIncludeReport(enabled);
  };

  const handleToggleDocument = (docId: string, enabled: boolean) => {
    setIncludedDocuments(prev => {
      const newSet = new Set(prev);
      if (enabled) {
        newSet.add(docId);
      } else {
        newSet.delete(docId);
      }
      return newSet;
    });
  };

  const handleToggleChatHistory = (enabled: boolean) => {
    setIncludeChatHistory(enabled);
  };

  const handleTogglePastReport = (reportId: string, enabled: boolean) => {
    setIncludedPastReports(prev => {
      const newSet = new Set(prev);
      if (enabled) {
        newSet.add(reportId);
      } else {
        newSet.delete(reportId);
      }
      return newSet;
    });
  };

  const handleCitationClick = async (citation: any) => {
    console.log('🔍 Citation clicked:', citation);
    setClickedCitation(citation);
    
    // Handle quote citations using the backend search
    if (citation.type === 'quote') {
      try {
        console.log(`🔍 Searching for quote: "${citation.text}"`);
        
        // Search through all available documents for this quote
        let foundInDocument = null;
        const token = localStorage.getItem('riskninja-token');
        const apiUrl = process.env.REACT_APP_API_URL || '';
        
        if (!token) {
          alert('Please log in to view citations');
          return;
        }

        // Try searching in each document from the workflow
        for (const doc of workflowData?.documents || []) {
          try {
            console.log(`🔍 Searching in document: ${doc.name} (${doc.id})`);
            const searchResponse = await fetch(`${apiUrl}/api/documents/${doc.id}/quote-search?quote=${encodeURIComponent(citation.text)}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (searchResponse.ok) {
              const searchResult = await searchResponse.json();
              if (searchResult.found) {
                foundInDocument = {
                  documentId: doc.id,
                  documentName: doc.name,
                  pageNumber: searchResult.pageNumber,
                  confidence: searchResult.confidence,
                  startOffset: searchResult.startOffset,
                  endOffset: searchResult.endOffset,
                  text: citation.text
                };
                console.log(`✅ Quote found in ${doc.name} on page ${searchResult.pageNumber} with ${Math.round((searchResult.confidence || 0) * 100)}% confidence`);
                break;
              }
            }
          } catch (docError) {
            console.warn(`⚠️ Error searching in document ${doc.name}:`, docError);
          }
        }
        
        if (foundInDocument) {
          // Set up citation for PDF viewer
          setSelectedCitation({
            documentId: foundInDocument.documentId,
            documentName: foundInDocument.documentName,
            page: foundInDocument.pageNumber,
            startOffset: foundInDocument.startOffset,
            endOffset: foundInDocument.endOffset,
            text: foundInDocument.text
          });
          
          // Show the PDF viewer
          setShowPdfViewer(true);
        } else {
          alert(`Quote not found in any document: "${citation.text.substring(0, 100)}..."`);
        }
        
      } catch (error) {
        console.error('❌ Error searching for quote citation:', error);
        alert('Error searching for citation. Please try again.');
      }
      return;
    }
    
    // Handle legacy offset citation formats  
    if (typeof citation === 'string' && citation.startsWith('CITATION:')) {
      // Legacy offset citation format: CITATION:doc_id,page,start,end
      const citationParts = citation.substring(9).split(',');
      if (citationParts.length >= 4) {
        const [docId, page, start, end] = citationParts;
        console.log(`Legacy citation: doc ${docId}, page ${page}, ${start}-${end}`);
        
        // Find document name
        const doc = workflowData?.documents.find(d => d.id === docId);
        const documentName = doc?.name || 'Unknown Document';
        
        setSelectedCitation({
          documentId: docId,
          documentName: documentName,
          page: parseInt(page),
          startOffset: parseInt(start),
          endOffset: parseInt(end),
          text: '[Legacy citation]'
        });
        
        setShowPdfViewer(true);
      }
    }
  };

  // Resolve a citation's docId to one of this report's documents. Order:
  // exact/partial id → filename (docName) → doc_N position → the only
  // document. The name step covers reports where doc_N labels were shifted
  // by a prepended Known Facts block, so doc_N no longer lines up with the
  // documents array.
  const resolveCitationDocument = (docId?: string, docName?: string) => {
    const docs = workflowData?.documents || [];
    if (docId) {
      const byId = docs.find(d => d.id === docId || d.id.includes(docId) || docId.includes(d.id));
      if (byId) return byId;
    }
    if (docName) {
      const wanted = docName.trim().toLowerCase();
      const byName = docs.find(d => (d.name || '').trim().toLowerCase() === wanted);
      if (byName) return byName;
    }
    if (docId && /^doc_\d+$/.test(docId)) {
      const byIndex = docs[parseInt(docId.replace('doc_', ''), 10) - 1];
      if (byIndex) return byIndex;
    }
    if (docs.length === 1 && docId !== 'additional_info') return docs[0];
    return undefined;
  };

  // Universal JSON citation click handler
  const handleUniversalCitationClick = (citation: any) => {
    console.log('🔗 Universal citation clicked:', citation);
    
    // Handle page-based citations from UniversalJsonRenderer
    if (citation.type === 'page' && citation.page) {
      const docId = citation.documentId || citation.sourceDocId || citation.docId;
      const citedName = citation.documentName || citation.docName;
      if (docId === 'additional_info' || (citedName || '').startsWith('Known Facts')) {
        alert('This citation points to the facts Risk Ninja extracted from your documents (Known Facts), not to a page in an uploaded PDF.');
        return;
      }
      const doc = resolveCitationDocument(docId, citedName);

      if (doc) {
        console.log(`📄 Opening document ${doc.name} at page ${citation.page}`);
        setSelectedCitation({
          documentId: doc.id,
          documentName: citation.documentName || doc.name,
          page: typeof citation.page === 'string' ? parseInt(citation.page) : citation.page,
          section: citation.section,
          text: citation.text || '',
          // Preserve section-local siblings so the modal can render prev/next.
          siblings: citation.siblings,
          siblingIndex: citation.siblingIndex,
        });
        setShowPdfViewer(true);
      } else {
        console.error('❌ Document not found for citation:', docId);
        alert(`Could not find document: ${citation.documentName || docId}`);
      }
    }
    // Handle quote-based citations from UniversalJsonRenderer
    else if (citation.type === 'quote' && citation.text) {
      // Use the existing quote search logic
      handleCitationClick({
        type: 'quote',
        text: citation.text,
        found: true
      });
    } else {
      // Fallback to workflowService for other types
      workflowService.handleCitationClick(citation, navigate);
    }
  };

  const handleSendChatMessage = async (message: string, displayMessage?: string) => {
    try {
      setSaveStatus('saving');

      // Add user message to chat (use displayMessage for UI, message for backend)
      const userMessage = {
        id: `user-${Date.now()}`,
        sender: 'user',
        content: displayMessage || message, // Show title in chat, send full prompt to backend
        timestamp: new Date()
      };
      
      console.log('💬 Adding to chat history:', userMessage.content);
      console.log('🚀 Sending to backend:', message.substring(0, 100) + '...');
      
      setChatHistory(prev => [...prev, userMessage]);

      // Prepare enhanced context with report and documents (filtered by user controls)
      const allDocuments = [
        ...(workflowData?.documents || []).map(doc => ({id: doc.id, name: doc.name})),
        ...additionalDocuments.filter(doc => doc.status === 'completed').map(doc => ({id: doc.id, name: doc.name}))
      ];
      
      // Filter documents based on user selection
      const filteredDocuments = allDocuments.filter(doc => includedDocuments.has(doc.id));

      // Prepare report content
      let reportContent = '';
      if (workflowData?.content) {
        if (typeof workflowData?.content === 'string') {
          reportContent = workflowData?.content;
        } else {
          // Handle JSON report content
          reportContent = JSON.stringify(workflowData?.content, null, 2);
        }
      }

      // Use new dedicated chat workflow API
      const token = localStorage.getItem('riskninja-token');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      
      if (!token) {
        throw new Error('Authentication required');
      }

      // Create AI message placeholder for streaming
      const aiMessageId = `ai-${Date.now()}`;
      setChatHistory(prev => [...prev, {
        id: aiMessageId,
        sender: 'ai',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
        citations: []
      }]);

      // Prepare request payload.
      // `message` is the full prompt body the AI sees. When the user picked a
      // template, `displayMessage` carries the template title and the backend
      // persists that instead of `message` to keep template prompts out of chat
      // history (template prompts are classified IP).
      const requestPayload = {
        message: message,
        displayMessage: displayMessage,
        sessionId: currentSessionId,
        customerId: workflowData?.customerId,
        reportId: workflowData?.reportId,
        projectId: workflowData?.projectId,
        isFirstMessage: isFirstMessage,
        sessionTitle: sessionTitle,
        forceNewSession: forceNewSession,
        reportContent: reportContent,
        reportMetadata: {
          workflowType: workflowData?.workflowType,
          customerName: workflowData?.customerName,
          customerId: workflowData?.customerId,
          projectId: workflowData?.projectId,
          documents: filteredDocuments,
          isRegenerated: workflowData?.isRegenerated || false,
          documentOrder: workflowData?.isRegenerated
            ? workflowData?.documents?.map((doc: any, index: number) => ({
                id: doc.id,
                name: doc.name,
                originalIndex: index
              }))
            : undefined
        },
        documents: filteredDocuments.length > 0 ? filteredDocuments : [],
        pastReportIds: pastReports.filter(r => includedPastReports.has(r.id)).map(r => r.id),
        chatHistory: includeChatHistory ? chatHistory.slice(-10) : [],
        includeReport: includeReport
      };

      // Try streaming endpoint first, fallback to non-streaming if not available
      console.log('🌊 Starting chat (with streaming if available)...');
      let useStreaming = true;
      let response = await fetch(`${apiUrl}/api/chat/workflow/stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestPayload)
      });

      // Check if streaming endpoint exists, fallback to non-streaming if 404
      if (response.status === 404) {
        console.log('⚠️ Streaming endpoint not available, falling back to non-streaming');
        useStreaming = false;

        // Use old non-streaming endpoint
        response = await fetch(`${apiUrl}/api/chat/workflow`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestPayload)
        });
      }

      if (!response.ok) {
        throw new Error(`Chat failed: ${response.status}`);
      }

      if (useStreaming) {
        // Read streaming response
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let fullResponse = '';
        let sessionIdFromStream: string | null = null;
        let sessionTitleFromStream: string | null = null;
        let buffer = ''; // Buffer to accumulate partial SSE lines across chunks

        if (!reader) {
          throw new Error('Stream reader not available');
        }

        try {
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              console.log('✅ Stream complete');
              // Process any remaining buffered data
              if (buffer.trim() && buffer.startsWith('data: ')) {
                try {
                  const data = JSON.parse(buffer.slice(6));
                  if (data.type === 'done') {
                    sessionIdFromStream = data.sessionId;
                    setChatHistory(prev => prev.map(msg =>
                      msg.id === aiMessageId
                        ? { ...msg, isStreaming: false }
                        : msg
                    ));
                  }
                } catch (e) {
                  console.warn('Failed to parse final buffered SSE data');
                }
              }
              break;
            }

            // Decode chunk and add to buffer
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            // Split by newlines but keep track of incomplete lines
            const lines = buffer.split('\n');

            // The last element might be incomplete (no trailing newline), so keep it in buffer
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));

                  if (data.type === 'chunk') {
                    // Append chunk to message content
                    fullResponse += data.content;

                    setChatHistory(prev => prev.map(msg =>
                      msg.id === aiMessageId
                        ? { ...msg, content: fullResponse }
                        : msg
                    ));
                  } else if (data.type === 'done') {
                    // Streaming complete
                    console.log('📊 Stream metadata:', data.usageMetadata);
                    sessionIdFromStream = data.sessionId;

                    setChatHistory(prev => prev.map(msg =>
                      msg.id === aiMessageId
                        ? { ...msg, isStreaming: false }
                        : msg
                    ));
                  } else if (data.type === 'title') {
                    // Session title update
                    sessionTitleFromStream = data.sessionTitle;
                    setSessionTitle(data.sessionTitle);
                  } else if (data.type === 'error') {
                    // Error from backend
                    throw new Error(data.error || 'Streaming error');
                  }
                } catch (parseError) {
                  console.warn('Failed to parse SSE data:', line, parseError);
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }

        // Update session info
        if (sessionIdFromStream) {
          setCurrentSessionId(sessionIdFromStream);
        }
        if (sessionTitleFromStream) {
          setSessionTitle(sessionTitleFromStream);
        }

        // Reset UI state
        setIsFirstMessage(false);
        setForceNewSession(false);
        setSaveStatus('saved');
      } else {
        // Non-streaming response (old endpoint)
        const result = await response.json();

        if (result.success) {
          // Update AI message with response
          setChatHistory(prev => prev.map(msg =>
            msg.id === aiMessageId
              ? { ...msg, content: result.response, isStreaming: false }
              : msg
          ));

          // Update session info
          if (result.sessionId) {
            setCurrentSessionId(result.sessionId);
          }
          if (result.sessionTitle) {
            setSessionTitle(result.sessionTitle);
          }

          // Reset UI state
          setIsFirstMessage(false);
          setForceNewSession(false);
          setSaveStatus('saved');
        } else {
          throw new Error(result.error || 'Chat failed');
        }
      }
      
    } catch (error) {
      console.error('Error sending chat message:', error);
      setSaveStatus('error');

      // Remove streaming placeholder and add error message
      setChatHistory(prev => {
        const filtered = prev.filter(msg => !msg.isStreaming);
        return [...filtered, {
          id: `error-${Date.now()}`,
          sender: 'ai',
          content: 'Sorry, I encountered an error processing your message. Please try again.',
          timestamp: new Date()
        }];
      });
    }
  };

  const handleClearChatHistory = () => {
    setChatHistory([]);
    // Fully reset session — without forceNewSession + URL cleanup the next
    // message would re-attach to the old session on the backend, making the
    // "cleared" conversation come back stitched onto whatever the user types
    // next. (Bug: the user saw "hello → reply → clear → new message" join
    // into a single concatenated conversation.)
    setCurrentSessionId(null);
    setSessionTitle(null);
    setIsFirstMessage(true);
    setSaveStatus('saved');
    setForceNewSession(true);
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.has('sessionId')) {
      urlParams.delete('sessionId');
      const newUrl = `${location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  };

  const handleStartNewChat = () => {
    // Force create a new session by clearing current session state
    setChatHistory([]);
    setCurrentSessionId(null);
    setSessionTitle(null);
    setIsFirstMessage(true);
    setSaveStatus('saved');
    setForceNewSession(true); // Force backend to create new session

    // Remove sessionId from URL if present to prevent auto-loading
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.has('sessionId')) {
      urlParams.delete('sessionId');
      const newUrl = `${location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  };

  const handleSelectConversation = (sessionId: string) => {
    if (sessionId === currentSessionId) return;
    const urlParams = new URLSearchParams(location.search);
    urlParams.set('sessionId', sessionId);
    navigate(`${location.pathname}?${urlParams.toString()}`, { replace: true });
  };

  const handleAdditionalDocuments = (newDocuments: DroppedDocument[]) => {
    console.log('📁 Additional documents added:', newDocuments);
    setAdditionalDocuments(prev => [...prev, ...newDocuments]);

    // Uploaded documents join the chat context immediately and are
    // pre-selected in the regenerate modal (the user can deselect them there).
  };

  // Removing a completed upload takes it out of this report's context (chat +
  // regenerate modal). The file itself already lives in the project, so ask
  // whether it should be deleted there too.
  const handleAdditionalDocumentRemoved = async (documentId: string) => {
    const removed = additionalDocuments.find(doc => doc.id === documentId);
    setAdditionalDocuments(prev => prev.filter(doc => doc.id !== documentId));

    const alsoDelete = window.confirm(
      `Also delete "${removed?.name || 'this file'}" from the project?\n\n` +
      'OK deletes the file from the project. Cancel keeps the file in the project ' +
      "but removes it from this report's context."
    );

    if (!alsoDelete) return;

    try {
      await workflowService.deleteDocument(documentId);
    } catch (error) {
      console.error('Failed to delete document from project:', error);
      alert(
        `The file was removed from this report's context, but deleting it from the project failed: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  };

  const handleRegenerateReport = async (selection: RegenerateSelection) => {
    try {
      setIsRegenerating(true);
      setRegenerateProgress(0);
      setShowRegenerateModal(false);

      // Separate selected documents by type
      const selectedOriginalDocIds = selection.selectedDocumentIds.filter(id => 
        workflowData?.documents.some(doc => doc.id === id)
      );
      
      // Everything that isn't an original report document rides along as an
      // "additional" ID: freshly uploaded documents plus anything picked from
      // the customer's other project documents. The backend just flattens
      // both lists and loads by ID.
      const selectedAdditionalDocIds = selection.selectedDocumentIds.filter(id =>
        !selectedOriginalDocIds.includes(id)
      );

      // Filter documents based on selection
      const selectedOriginalDocs = workflowData?.documents.filter(doc => 
        selectedOriginalDocIds.includes(doc.id)
      );
      
      const selectedAdditionalDocs = additionalDocuments
        .filter(doc => doc.status === 'completed' && selectedAdditionalDocIds.includes(doc.id))
        .map(doc => ({
          id: doc.id,
          name: doc.name,
          size: doc.size
        }));

      const chatToInclude = selection.includeChatHistory ? chatHistory : [];

      console.log('🔄 Starting selective report regeneration...', {
        selectedOriginalDocs: selectedOriginalDocs?.length || 0,
        selectedAdditionalDocs: selectedAdditionalDocs?.length || 0,
        totalOriginalDocs: workflowData?.documents.length,
        totalAdditionalDocs: additionalDocuments.filter(d => d.status === 'completed').length,
        chatIncluded: selection.includeChatHistory,
        chatMessages: chatToInclude.length,
        workflowType: selection.workflowType
      });

      const regenerationData = {
        originalWorkflowData: {
          ...workflowData,
          documents: selectedOriginalDocs // Only selected original documents
        },
        selectedDocuments: {
          originalDocIds: selectedOriginalDocIds,
          additionalDocIds: selectedAdditionalDocIds
        },
        additionalDocuments: selectedAdditionalDocs,
        includeChatHistory: selection.includeChatHistory,
        chatHistory: chatToInclude,
        // CRITICAL FIX: Include project ID for document uploads
        projectId: workflowData?.projectId,
        customerId: workflowData?.customerId,
        options: {
          preserveStructure: true,
          enhanceCitations: true,
          workflowType: selection.workflowType
        }
      };

      const result = await workflowService.regenerateReport(regenerationData, (progress) => {
        setRegenerateProgress(progress);
      });

      if (result.success && result.report) {
        // Navigate to the new regenerated report
        navigate('/workflow-report', {
          state: {
            // Explicit `false` means the auto-save failed server-side — the
            // content exists only in this page's state.
            reportNotPersisted: result.report.persisted === false,
            content: result.report.content,
            workflowType: result.report.workflowType,
            documents: result.report.documents,
            customerId: result.report.customerId,
            customerName: result.report.customerName,
            // CRITICAL FIX: Preserve project ID in regenerated report
            projectId: workflowData?.projectId,
            isRegenerated: true,
            originalReportData: workflowData,
            selectedDocumentsCount: selection.selectedDocumentIds.length,
            chatMessagesIncluded: chatToInclude.length,
            regenerationSelection: selection
          }
        });
      } else {
        throw new Error('Regeneration failed - no report returned');
      }

    } catch (error) {
      console.error('Error regenerating report:', error);
      alert(`Failed to regenerate report: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsRegenerating(false);
      setRegenerateProgress(0);
    }
  };

  // `envelopeOverride`: BETA suite reports pass their live envelope (it can
  // include enhancements merged after this page loaded).
  const handleExportPDF = async (envelopeOverride?: any) => {
    try {
      console.log('📄 Opening PDF export preview window...');

      if (!workflowData) {
        alert('No report data available for export');
        return;
      }

      // Prepare data for export preview
      console.log('📦 WorkflowReport: Preparing export data:', {
        hasWorkflowData: !!workflowData,
        workflowType: workflowData?.workflowType,
        isUniversalJson: workflowData?.isUniversalJson,
        hasUniversalJsonResponse: !!workflowData?.universalJsonResponse,
        hasContent: !!workflowData?.content,
        contentLength: workflowData?.content?.length,
        contentPreview: workflowData?.content?.substring(0, 100),
        sectionsCount: reportSections.length,
        universalJsonResponseKeys: workflowData?.universalJsonResponse ? Object.keys(workflowData.universalJsonResponse) : []
      });

      // If workflowData has content but no universalJsonResponse, try to parse it
      let enhancedWorkflowData = workflowData;
      if (envelopeOverride && typeof envelopeOverride === 'object' && envelopeOverride.responseMetadata) {
        enhancedWorkflowData = { ...workflowData, universalJsonResponse: envelopeOverride, isUniversalJson: true };
      } else if (workflowData && workflowData.content && !workflowData.universalJsonResponse) {
        console.log('🔍 Attempting to parse content as JSON for export...');
        try {
          const parsed = JSON.parse(workflowData.content);
          const hasUniversalStructure =
            parsed.responseMetadata ||
            parsed.executiveSummary ||
            parsed.analysisMatrices ||
            parsed.keyFindings ||
            parsed.riskManagementMatrix ||
            parsed.trendAnalysis ||
            (parsed.analysisMatrices || parsed.criticalAlerts) ||
            (parsed.namedInsuredProfile && parsed.actionableRecommendations);

          if (hasUniversalStructure) {
            console.log('✅ Content is Universal JSON structure, adding to workflowData');
            enhancedWorkflowData = {
              ...workflowData,
              universalJsonResponse: parsed,
              isUniversalJson: true
            };
          } else {
            console.log('❌ Content is JSON but not Universal JSON structure');
          }
        } catch (e) {
          console.log('❌ Content is not valid JSON');
        }
      }

      // Clean up ALL old pdfExport entries to free up localStorage space
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('pdfExport_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      const exportData = {
        workflowData: enhancedWorkflowData,
        sections: reportSections
      };

      // Generate a unique key for this export
      const exportKey = `pdfExport_${Date.now()}`;

      // Compress and store data to handle large reports
      const LZString = await import('lz-string');
      const compressed = LZString.compressToUTF16(JSON.stringify(exportData));
      localStorage.setItem(exportKey, compressed);
      localStorage.setItem(`${exportKey}_compressed`, 'true');

      // Also store the token separately
      const token = localStorage.getItem('riskninja-token');

      // Open new window with PDF export preview V2, passing the key and token as URL parameters
      const exportWindow = window.open(
        `/pdf-export-preview-v2?key=${exportKey}&token=${encodeURIComponent(token || '')}`,
        '_blank',
        'width=1400,height=900,scrollbars=yes,resizable=yes'
      );

      if (!exportWindow) {
        alert('Please allow popups to use the PDF export feature');
        localStorage.removeItem(exportKey);
        return;
      }

      console.log('✅ PDF export preview window opened successfully');
    } catch (error) {
      console.error('❌ Failed to open export preview:', error);
      alert(`Failed to open export preview: ${error instanceof Error ? error.message : 'Unknown error'}`);
      sessionStorage.removeItem('pdfExportData');
    }
  };

  const handleExportDOC = async () => {
    try {
      console.log('📄 Starting Word export for workflow report...');
      
      // Get the report content from workflowData
      const reportContent = workflowData?.content || 
                           workflowData?.universalJsonResponse ||
                           'No content available for export';
      
      // Generate filename based on workflow type and date
      const workflowType = workflowData?.workflowType || 'report';
      const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const filename = `${workflowType.replace(/_/g, '-')}-${timestamp}.docx`;
      
      console.log('📄 Exporting with filename:', filename);
      
      // Use the workflowService exportReport method (backend should handle format)
      await workflowService.exportReport({
        content: reportContent,
        workflowType: workflowData?.workflowType,
        documents: workflowData?.documents || [],
        customerId: workflowData?.customerId,
        reportTitle: workflowData?.reportTitle,
        format: 'docx' // Specify Word format
      }, filename);
      
      console.log('✅ Word export completed successfully');
    } catch (error) {
      console.error('❌ Word export failed:', error);
      alert(`Word export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Generate PDF as base64 for AMS360 attachment
  const generatePdfBase64 = async (): Promise<string> => {
    console.log('📄 Generating PDF as base64 for AMS360...');

    if (!reportContentRef.current) {
      throw new Error('Report content not available');
    }

    // Use html2pdf.js to generate PDF
    const element = reportContentRef.current;

    const opt = {
      margin: 10,
      filename: 'report.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const }
    };

    // Generate PDF and get as blob
    const pdf = await html2pdf().set(opt).from(element).outputPdf('blob');

    // Convert blob to base64
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        // Remove the data:application/pdf;base64, prefix
        const base64Data = base64.split(',')[1];
        console.log('✅ PDF generated as base64, length:', base64Data?.length);
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(pdf as Blob);
    });
  };

  // Check if we're in a loading state or need to show loading UI
  const urlParams = new URLSearchParams(location.search);
  const hasReportId = urlParams.has('reportId');
  const hasSessionId = urlParams.has('sessionId');

  // Show loading if we're actively loading report data OR if we have reportId but no workflowData yet
  // However, if we have a sessionId, prioritize session loading over report loading
  const shouldShowLoading = isLoadingReportData || (!workflowData && hasReportId && !hasSessionId);

  // ALWAYS render something - never return early with nothing
  // Allow existing chat sessions to render even without complete workflowData
  if (shouldShowLoading || (!workflowData && !hasSessionId)) {
    // ALWAYS show a loading/waiting state - NEVER a blank page
    return (
      <div className="w-full min-h-screen bg-slate-50 dark:bg-dark-bg p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {hasSessionId && hasReportId ? 'Resuming Chat Session' : 'Loading Workflow Report'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {hasSessionId && hasReportId
                  ? 'Loading your previous conversation and associated report...'
                  : 'The report is being prepared. This may take a moment for research workflows.'
                }
              </p>
              <div className="space-y-3 mt-6">
                <button
                  onClick={() => window.location.reload()}
                  className="w-full max-w-xs mx-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Refresh Page
                </button>
                <button
                  onClick={() => {
                    if (workflowData?.projectId && workflowData?.customerId) {
                      navigate(`/?customerId=${workflowData?.customerId}&projectId=${workflowData?.projectId}&view=project-details`);
                    } else if (workflowData?.customerId) {
                      navigate(`/?customerId=${workflowData?.customerId}`);
                    } else {
                      navigate('/');
                    }
                  }}
                  className="w-full max-w-xs mx-auto px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Back to Project Folder
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                If the report doesn't load after refreshing, try navigating back and running the workflow again.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Insurance summary reports are rendered by InsuranceSummaryDetail inside the
  // project view, not by this generic workflow report page. Redirect to the
  // project's Insurance Summary tab with the specific summary pre-opened.
  if (
    workflowData?.workflowType === 'insurance_summary' &&
    workflowData?.customerId &&
    workflowData?.projectId
  ) {
    const summaryId = workflowData.reportId || '';
    const url =
      `/?customerId=${encodeURIComponent(workflowData.customerId)}` +
      `&projectId=${encodeURIComponent(workflowData.projectId)}` +
      `&view=project-details` +
      `&customerDetailView=insurance_summary` +
      (summaryId ? `&summaryId=${encodeURIComponent(summaryId)}` : '');
    navigate(url, { replace: true });
    return null;
  }

  const getWorkflowTitle = () => {
    // Hard-coded workflow title mapping
    const workflowTitleMap: Record<string, string> = {
      ...BETA_TOOL_LABELS,
      'document_comparison': 'Document Comparison Report',
      'renewal': 'Renewal Analysis Report',
      'renewal_analysis': 'Renewal Analysis Report',
      'year_over_year': 'Year-Over-Year Analysis Report',
      'yoy_renewal': 'Year-Over-Year Renewal Report',
      'personal_lines_yoy_renewal': 'Personal Lines YOY Renewal Report',
      'commercial_lines_exec_renewal': 'Commercial Lines Executive Renewal Report',
      'loss_run': 'Loss Run Analysis Report',
      'loss_run_analysis': 'Loss Run Analysis Report',
      'loss_run_analyzer': 'Loss Run Analysis Report',
      'loss_run_analyzer_v4': 'Loss Run Analysis Report (NEW)',
      'loss_run_analyzer_v4_3': 'Loss Run Analysis Report 4.3',
      'coverage_analysis': 'Coverage Analysis Report',
      'insurance_requirements': 'Insurance Requirements Analysis',
      'certificate_of_effort': 'Certificate of Effort Report',
      'research_tool': 'Research Analysis Report',
      'underwriting': 'Underwriting Analysis Report'
    };

    // Extract workflow type from multiple sources (priority order)
    const extractWorkflowType = (): string | null => {
      console.log('🔍 extractWorkflowType: Starting extraction...', {
        workflowType: workflowData?.workflowType,
        hasUniversalJsonResponse: !!workflowData?.universalJsonResponse,
        contentType: typeof workflowData?.content,
        contentLength: typeof workflowData?.content === 'string' ? workflowData.content.length : 'N/A'
      });

      // 1. Direct workflowType if not 'existing_report'
      if (workflowData?.workflowType && workflowData.workflowType !== 'existing_report') {
        console.log('🔍 extractWorkflowType: Found direct workflowType:', workflowData.workflowType);
        return workflowData.workflowType;
      }

      // 2. From universalJsonResponse.responseMetadata
      const fromUniversalJson = workflowData?.universalJsonResponse?.responseMetadata?.workflowType;
      if (fromUniversalJson) {
        console.log('🔍 extractWorkflowType: Found from universalJsonResponse:', fromUniversalJson);
        return fromUniversalJson;
      }

      // 3. Parse content string to extract responseMetadata.workflowType
      try {
        if (typeof workflowData?.content === 'string') {
          // Clean up the content string before parsing
          let jsonString = workflowData.content.trim();
          if (jsonString.startsWith('```json')) {
            jsonString = jsonString.replace(/^```json\s*\n?/, '').replace(/\n?```\s*$/, '');
          } else if (jsonString.startsWith('```')) {
            jsonString = jsonString.replace(/^```\s*\n?/, '').replace(/\n?```\s*$/, '');
          }
          const jsonStart = jsonString.indexOf('{');
          const jsonEnd = jsonString.lastIndexOf('}');
          if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
            jsonString = jsonString.substring(jsonStart, jsonEnd + 1);
          }
          jsonString = jsonString.replace(/,(\s*[\]}])/g, '$1');

          const parsed = JSON.parse(jsonString);
          console.log('🔍 extractWorkflowType: Parsed content, responseMetadata:', parsed?.responseMetadata);
          if (parsed?.responseMetadata?.workflowType) {
            console.log('🔍 extractWorkflowType: Found from parsed content:', parsed.responseMetadata.workflowType);
            return parsed.responseMetadata.workflowType;
          }

          // Also check data structure while we have the parsed content
          if (parsed) {
            if (parsed.complianceMatrix || parsed.executiveSummary?.overallStatus) {
              console.log('🔍 extractWorkflowType: Detected insurance_requirements from structure');
              return 'insurance_requirements';
            }
            if (parsed.executiveRenewalSummary || parsed.riskIntelligenceReport) {
              console.log('🔍 extractWorkflowType: Detected commercial_lines_exec_renewal from structure');
              return 'commercial_lines_exec_renewal';
            }
            if (parsed.lossRunSummary || parsed.detailedClaimsList || parsed.severityBreakdown) {
              console.log('🔍 extractWorkflowType: Detected loss_run_analysis from structure');
              return 'loss_run_analysis';
            }
            if (parsed.yearOverYearAnalysis || parsed.trendAnalysis) {
              console.log('🔍 extractWorkflowType: Detected year_over_year from structure');
              return 'year_over_year';
            }
            if (parsed.portfolioStructure && parsed.portfolioSynthesis) {
              console.log('🔍 extractWorkflowType: Detected portfolio_analyzer from structure');
              return 'portfolio_analyzer';
            }
          }
        }
      } catch (e) {
        console.log('🔍 extractWorkflowType: Failed to parse content:', e);
      }

      // 4. Detect workflow type from universalJsonResponse data structure
      const data = workflowData?.universalJsonResponse;
      if (data) {
        if (data.complianceMatrix || data.executiveSummary?.overallStatus) {
          console.log('🔍 extractWorkflowType: Detected insurance_requirements from universalJsonResponse');
          return 'insurance_requirements';
        }
        if (data.executiveRenewalSummary || data.riskIntelligenceReport) {
          console.log('🔍 extractWorkflowType: Detected commercial_lines_exec_renewal from universalJsonResponse');
          return 'commercial_lines_exec_renewal';
        }
        if (data.lossRunSummary || data.detailedClaimsList || data.severityBreakdown) {
          console.log('🔍 extractWorkflowType: Detected loss_run_analysis from universalJsonResponse');
          return 'loss_run_analysis';
        }
        if (data.yearOverYearAnalysis || data.trendAnalysis) {
          console.log('🔍 extractWorkflowType: Detected year_over_year from universalJsonResponse');
          return 'year_over_year';
        }
        if (data.portfolioStructure && data.portfolioSynthesis) {
          console.log('🔍 extractWorkflowType: Detected portfolio_analyzer from universalJsonResponse');
          return 'portfolio_analyzer';
        }
      }

      console.log('🔍 extractWorkflowType: No workflow type found');
      return null;
    };

    const effectiveWorkflowType = extractWorkflowType();

    console.log('🏷️ getWorkflowTitle RESULT:', {
      originalWorkflowType: workflowData?.workflowType,
      effectiveWorkflowType,
      hasUniversalJsonResponse: !!workflowData?.universalJsonResponse,
      customerName: workflowData?.customerName,
      reportTitle: (workflowData as any)?.reportTitle,
      willUseTitleFromMap: effectiveWorkflowType && workflowTitleMap[effectiveWorkflowType] ? workflowTitleMap[effectiveWorkflowType] : 'N/A'
    });

    // Get base title from map, or fall back to report title or generic
    let baseTitle = 'Workflow Report';

    if (effectiveWorkflowType && workflowTitleMap[effectiveWorkflowType]) {
      baseTitle = workflowTitleMap[effectiveWorkflowType];
    } else if ((workflowData as any)?.reportTitle) {
      // Use report title if it doesn't contain 'existing_report'
      const reportTitle = (workflowData as any).reportTitle;
      if (!reportTitle.includes('existing_report')) {
        baseTitle = reportTitle;
      }
    }

    // Combine with customer name
    if (workflowData?.customerName) {
      return `${workflowData.customerName} - ${baseTitle}`;
    }

    return baseTitle;
  };

  // SAFETY: Wrap entire component in try-catch to ALWAYS render something
  try {
    console.log('🔄 WorkflowReport: Starting render with data:', {
      hasData: !!workflowData,
      workflowType: workflowData?.workflowType,
      hasContent: !!workflowData?.content,
      isUniversalJson: workflowData?.isUniversalJson,
      hasUniversalResponse: !!workflowData?.universalJsonResponse
    });
    
    return (
      <>
        <style>
        {`
          @media print {
            /* Page setup */
            @page {
              size: letter;
              margin: 0.75in;
            }
            
            /* Hide non-content elements */
            .no-print,
            button,
            .export-controls,
            .navigation,
            .sidebar,
            .header,
            .footer,
            .chat-interface,
            .pdf-viewer,
            .document-viewer,
            [role="button"],
            .cursor-pointer {
              display: none !important;
            }
            
            /* Reset backgrounds and colors */
            body {
              background: white !important;
              color: black !important;
              font-size: 11pt !important;
              line-height: 1.5 !important;
            }
            
            /* Ensure content uses full width */
            .container,
            .content-wrapper,
            .report-content {
              max-width: 100% !important;
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            
            /* Keep content blocks together */
            .matrix-container,
            .key-finding,
            .recommendation,
            .strategy,
            .risk-management,
            .executive-summary,
            .content-block,
            table,
            .bg-blue-50,
            .bg-yellow-50,
            .bg-green-50,
            .bg-red-50,
            .bg-purple-50,
            .bg-gray-50,
            .border,
            .rounded-lg {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              margin-bottom: 1em !important;
            }
            
            /* Table specific handling */
            table {
              width: 100% !important;
              border-collapse: collapse !important;
              page-break-inside: avoid !important;
            }
            
            thead {
              display: table-header-group !important;
            }
            
            tr {
              page-break-inside: avoid !important;
            }
            
            /* Heading handling */
            h1, h2, h3, h4, h5, h6 {
              page-break-after: avoid !important;
              page-break-inside: avoid !important;
              margin-top: 1em !important;
              margin-bottom: 0.5em !important;
            }
            
            /* Keep headings with content */
            h1 + *,
            h2 + *,
            h3 + *,
            h4 + *,
            h5 + *,
            h6 + * {
              page-break-before: avoid !important;
            }
            
            /* List handling */
            ul, ol {
              page-break-inside: avoid !important;
            }
            
            li {
              page-break-inside: avoid !important;
              orphans: 3;
              widows: 3;
            }
            
            /* Paragraph handling */
            p {
              orphans: 3;
              widows: 3;
              margin: 0.5em 0 !important;
            }
            
            /* Color preservation */
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            /* Box and border preservation */
            .bg-blue-50 { background-color: #eff6ff !important; }
            .bg-yellow-50 { background-color: #fefce8 !important; }
            .bg-green-50 { background-color: #f0fdf4 !important; }
            .bg-red-50 { background-color: #fef2f2 !important; }
            .bg-purple-50 { background-color: #faf5ff !important; }
            .bg-gray-50 { background-color: #f9fafb !important; }
            
            /* Force new page for major sections if needed */
            .force-page-break {
              page-break-before: always !important;
            }
            
            /* Prevent large elements from breaking */
            .keep-together {
              page-break-inside: avoid !important;
              display: block !important;
            }
          }
        `}
      </style>
      <div className="w-full min-h-screen bg-slate-50 dark:bg-dark-bg">
      {/* Sticky mini-header: appears once user scrolls past the main header */}
      <div
        className={`fixed top-0 left-0 right-0 z-30 transition-all duration-200 ${
          showStickyHeader ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'
        }`}
      >
        <div className="bg-white/95 dark:bg-dark-bg/95 backdrop-blur border-b border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="max-w-[95vw] mx-auto px-4 py-2 flex items-center gap-4">
            <h4 className="text-sm font-semibold text-secondary dark:text-dark-text truncate flex-1 min-w-0">
              {getWorkflowTitle()}
            </h4>
            <CitationToggle
              showCitations={showCitations}
              onToggle={toggleCitations}
              size="sm"
            />
          </div>
        </div>
      </div>
      {/* Unsaved-report warning — the AI content was generated but the
          auto-save failed, so it exists only in this page. */}
      {showUnsavedWarning && (
        <div className="max-w-[95vw] mx-auto px-4 pt-4 no-print">
          <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-4">
            <svg className="w-5 h-5 flex-shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                This report could not be saved
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                The analysis finished, but saving it to your reports failed. Export or copy
                the content before leaving this page — it won't be here when you come back.
              </p>
            </div>
            <button
              onClick={() => setShowUnsavedWarning(false)}
              className="flex-shrink-0 text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200"
              title="Dismiss"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-6 min-h-[800px] max-w-[95vw] mx-auto px-4">
        {/* Table of Contents Sidebar */}
        {reportSections.length > 0 && (
          <div className={`transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-0'} flex-shrink-0`}>
            <div className={`sticky top-4 bg-white dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden ${sidebarOpen ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center justify-between">
                  <span>Table of Contents</span>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    title="Close sidebar"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </h3>
              </div>
              <nav className="p-2 max-h-[calc(100vh-8rem)] overflow-y-auto">
                <ul className="space-y-1">
                  {reportSections
                    // Filter out phantom entries that have no matching DOM
                    // section — protects against TOC/renderer drift in
                    // workflows where the builder lists sections the
                    // renderer doesn't actually emit (or under a different id).
                    .filter((section) => !validSectionIds || validSectionIds.has(section.id))
                    .map((section) => {
                      const isActive = activeSectionId === section.id;
                      return (
                        <li key={section.id}>
                          <button
                            onClick={() => scrollToSection(section.id)}
                            aria-current={isActive ? 'location' : undefined}
                            className={`w-full text-left px-3 py-2 text-sm rounded transition-colors duration-150 border-l-2 ${
                              isActive
                                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200 border-blue-500 font-medium'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-700 border-transparent'
                            }`}
                          >
                            {section.title}
                          </button>
                        </li>
                      );
                    })}
                </ul>
              </nav>
            </div>
          </div>
        )}

        {/* Toggle button for closed sidebar */}
        {!sidebarOpen && reportSections.length > 0 && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="fixed left-4 top-4 z-10 bg-white dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg p-2 shadow-lg hover:shadow-xl transition-shadow duration-200"
            title="Open table of contents"
          >
            <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}

        {/* Main Content Area */}
        <div className={`flex-1 grid grid-cols-1 ${railOpen ? 'lg:grid-cols-3' : 'lg:grid-cols-1'} gap-6`}>
        {/* Left Column - Report (2/3 width when rail is open, full when closed) */}
        <div className={`${railOpen ? 'lg:col-span-2' : ''} bg-white dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden flex flex-col`}>
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-secondary dark:text-dark-text">
                  {getWorkflowTitle()}
                </h3>
                <p className="text-sm text-accent dark:text-dark-muted">
                  <span className="text-gray-500 dark:text-gray-400">Generated:</span>{' '}
                  {workflowData?.workflowType === 'existing_report' && (workflowData as any).createdAt ?
                    new Date((workflowData as any).createdAt).toLocaleDateString() :
                    new Date().toLocaleDateString()}
                </p>
              </div>
              
              {/* Export buttons + Citation Toggle + Rail collapse */}
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {/* Export buttons are portal'd in by UniversalJsonRenderer */}
                <div ref={exportSlotRef} className="flex items-center gap-1.5" />
                <CitationToggle
                  showCitations={showCitations}
                  onToggle={toggleCitations}
                  size="sm"
                />
                <button
                  onClick={() => setRailOpen((v) => !v)}
                  title={railOpen ? 'Hide chat panel' : 'Show chat panel'}
                  aria-label={railOpen ? 'Hide chat panel' : 'Show chat panel'}
                  className="p-1.5 rounded-full border border-gray-200 dark:border-gray-600 text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    {railOpen ? (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    )}
                  </svg>
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900 min-h-[600px]">
            <div className="w-full bg-white dark:bg-dark-bg shadow-lg rounded-lg p-8">
              <div className="text-base leading-relaxed text-secondary dark:text-dark-text">
                <div ref={reportContentRef} className="overflow-x-auto markdown-content">
                  {(() => {
                    console.log('🔍 WorkflowData content analysis:', {
                      contentType: typeof workflowData?.content,
                      isString: typeof workflowData?.content === 'string',
                      isObject: typeof workflowData?.content === 'object',
                      contentLength: workflowData?.content?.length || Object.keys(workflowData?.content || {}).length,
                      contentPreview: typeof workflowData?.content === 'string' 
                        ? workflowData?.content.substring(0, 200) 
                        : Object.keys(workflowData?.content || {}),
                      hasUniversalJson: workflowData?.isUniversalJson,
                      hasUniversalResponse: !!workflowData?.universalJsonResponse
                    });
                    return null;
                  })()}
                  {(() => {
                    const suiteWorkflowType = workflowData?.universalJsonResponse?.responseMetadata?.workflowType || workflowData?.workflowType;
                    return isSuiteWorkflowType(suiteWorkflowType);
                  })() ? (
                    <SuiteReportRenderer
                      data={workflowData?.universalJsonResponse || (typeof workflowData?.content === 'object' ? (workflowData?.content as any) : undefined)}
                      onCitationClick={handleUniversalCitationClick}
                      onExportPdf={handleExportPDF}
                      hideCitations={hideCitations}
                      customerName={workflowData?.customerName}
                      reportId={workflowData?.reportId}
                      reportTitle={workflowData?.reportTitle}
                    />
                  ) : workflowData?.isUniversalJson && workflowData?.universalJsonResponse ? (
                    (() => {
                      try {
                        console.log('🎨 ATTEMPTING UniversalJsonRenderer with data:', {
                          workflowType: workflowData?.workflowType,
                          dataType: typeof workflowData?.universalJsonResponse,
                          dataKeys: Object.keys(workflowData?.universalJsonResponse || {}),
                          hideCitations: hideCitations || workflowData?.workflowType === 'research_tool',
                          customerName: workflowData?.customerName,
                          documentsCount: (workflowData?.documents || []).length
                        });

                        console.log('🔗 WorkflowReport: Passing handleExportPDF to UniversalJsonRenderer');
                        console.log('🔗 WorkflowReport: handleExportPDF is:', typeof handleExportPDF);

                        const result = (
                          <UniversalJsonRenderer
                            data={workflowData?.universalJsonResponse}
                            onCitationClick={handleUniversalCitationClick}
                            hideCitations={hideCitations || workflowData?.workflowType === 'research_tool'} // Research has no citations
                            customerName={workflowData?.customerName}
                            documents={workflowData?.documents || []}
                            customPdfExportHandler={handleExportPDF}
                            workflowType={workflowData?.workflowType}
                            compactHeader={true}
                            exportPortalRef={exportSlotRef}
                          />
                        );

                        console.log('✅ UniversalJsonRenderer rendered successfully');
                        return result;
                        
                      } catch (renderError) {
                        console.error('🚨🚨🚨 CRITICAL: UniversalJsonRenderer FAILED 🚨🚨🚨');
                        console.error('Render error:', renderError);
                        console.error('Render error message:', renderError instanceof Error ? renderError.message : renderError);
                        console.error('Render error stack:', renderError instanceof Error ? renderError.stack : 'No stack');
                        console.error('Data that caused error:', JSON.stringify(workflowData?.universalJsonResponse, null, 2));
                        
                        // THROW the error so ErrorBoundary catches it and shows the real error
                        throw renderError;
                      }
                    })()
                  ) : workflowData?.content && typeof workflowData?.content === 'object' ? (
                    (() => {
                      // Check if the content matches the UniversalJsonRenderer interface
                      const content = workflowData?.content as any;
                      console.log('🔍 Object content detected:', content);
                      console.log('🔍 Object content keys:', Object.keys(content));
                      
                      // Debug logging to understand the structure
                      console.log('🔍 Content structure check:', {
                        hasResponseMetadata: !!content?.responseMetadata,
                        hasExecutiveSummary: !!content?.executiveSummary,
                        hasAnalysisMatrices: !!content?.analysisMatrices,
                        hasKeyFindings: !!content?.keyFindings,
                        hasCriticalAlerts: !!content?.criticalAlerts,
                        hasNamedInsuredProfile: !!content?.namedInsuredProfile
                      });
                      
                      const hasRequiredFields = (content?.responseMetadata &&
                                             content?.executiveSummary &&
                                             (content?.analysisMatrices || content?.criticalAlerts ||
                                              (content?.portfolioStructure && content?.portfolioSynthesis)) &&
                                             (content?.keyFindings || content?.namedInsuredProfile || content?.portfolioSynthesis)) ||
                                             // Portfolio analyzer - detected by structure alone
                                             (content?.portfolioStructure && content?.portfolioSynthesis);
                      
                      if (hasRequiredFields) {
                        // Convert year-over-year structure to UniversalJsonRenderer format
                        if (content.criticalAlerts && !content.analysisMatrices) {
                          console.log('🔄 Converting criticalAlerts to analysisMatrices');
                          content.analysisMatrices = [];
                          
                          // Convert each critical alert category to a matrix
                          Object.keys(content.criticalAlerts).forEach(alertType => {
                            const alerts = content.criticalAlerts[alertType];
                            if (Array.isArray(alerts) && alerts.length > 0) {
                              const title = alertType.replace(/([A-Z])/g, ' $1')
                                                   .replace(/^./, str => str.toUpperCase())
                                                   .replace('priority1_', 'Priority 1: ')
                                                   .replace('priority2_', 'Priority 2: ')
                                                   .replace('priority3_', 'Priority 3: ')
                                                   .replace('priority4_', 'Priority 4: ');
                              
                              // Create matrix with headers based on alert type
                              let headers = ['Item', 'Description', 'Impact'];
                              if (alertType.includes('premium')) {
                                headers = ['Change Type', 'Description', 'Expiring Value', 'Renewal Value', 'Impact'];
                              } else if (alertType.includes('coverage')) {
                                headers = ['Coverage', 'Change Type', 'Expiring Limit', 'Renewal Limit', 'Impact'];
                              } else if (alertType.includes('exclusion')) {
                                headers = ['Form Number', 'Title', 'Change Type', 'Impact'];
                              }
                              
                              const rows = alerts.map(alert => {
                                const rowData: any = {};
                                headers.forEach((header, index) => {
                                  switch(header.toLowerCase()) {
                                    case 'item':
                                    case 'change type':
                                      rowData[`col_${index}`] = alert.changeType || alert.exclusionType || alert.coveragePart || 'Change';
                                      break;
                                    case 'description':
                                      rowData[`col_${index}`] = alert.description || alert.title || '';
                                      break;
                                    case 'expiring value':
                                    case 'expiring limit':
                                      rowData[`col_${index}`] = alert.expiringValue || alert.expiringLimit || '';
                                      break;
                                    case 'renewal value':
                                    case 'renewal limit':
                                      rowData[`col_${index}`] = alert.renewalValue || alert.renewalLimit || '';
                                      break;
                                    case 'form number':
                                      rowData[`col_${index}`] = alert.formNumber || '';
                                      break;
                                    case 'title':
                                      rowData[`col_${index}`] = alert.title || '';
                                      break;
                                    case 'coverage':
                                      rowData[`col_${index}`] = alert.coveragePart || '';
                                      break;
                                    case 'impact':
                                      rowData[`col_${index}`] = alert.impact || alert.riskImplication || '';
                                      break;
                                    default:
                                      rowData[`col_${index}`] = '';
                                  }
                                });
                                return rowData;
                              });
                              
                              content.analysisMatrices.push({
                                title: title,
                                headers: headers,
                                rows: rows
                              });
                            }
                          });
                          
                          console.log(`✅ Created ${content.analysisMatrices.length} analysis matrices`);
                        }
                        
                        if (content.namedInsuredProfile && !content.keyFindings) {
                          console.log('🔄 Converting namedInsuredProfile to keyFindings');
                          const findings: any[] = [];
                          
                          if (content.namedInsuredProfile.changes) {
                            Object.keys(content.namedInsuredProfile.changes).forEach(changeType => {
                              const change = content.namedInsuredProfile.changes[changeType];
                              if (change !== 'No' && change !== false) {
                                findings.push({
                                  finding: {
                                    text: `${changeType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}: ${change}`,
                                    citations: []
                                  },
                                  implication: {
                                    text: 'Review the impact of this change on coverage and risk profile',
                                    citations: []
                                  }
                                });
                              }
                            });
                          }
                          
                          content.keyFindings = {
                            summary: {
                              text: content.executiveSummary?.paragraphs?.[0]?.content?.text || 'Year-over-year renewal analysis completed',
                              citations: []
                            },
                            findings: findings
                          };
                          
                          console.log(`✅ Created keyFindings with ${findings.length} findings`);
                        }
                        
                        // Ensure all required arrays exist and are properly initialized
                        if (!content.executiveSummary.paragraphs) {
                          content.executiveSummary.paragraphs = [];
                        }
                        if (!content.analysisMatrices) {
                          content.analysisMatrices = [];
                        }
                        if (!content.keyFindings.findings) {
                          content.keyFindings.findings = [];
                        }
                        if (!content.recommendations) {
                          content.recommendations = [];
                        }
                        
                        // Process embedded quote citations and convert to Citation objects
                        const processQuoteCitations = (text: string, docId: string = 'unknown') => {
                          const quoteCitationRegex = /\[CITE:\s*"([^"]+)"\]/g;
                          const citations: any[] = [];
                          let match;
                          let citationId = 1;
                          
                          while ((match = quoteCitationRegex.exec(text)) !== null) {
                            citations.push({
                              id: `cite-${citationId++}`,
                              sourceDocId: docId,
                              section: 'Policy Text',
                              page: 1,
                              text: match[1],
                              clickable: true,
                              url: `/documents/${docId}#page=1`
                            });
                          }
                          return citations;
                        };
                        
                        // Fix paragraph citations - extract from embedded text
                        content.executiveSummary.paragraphs = content.executiveSummary.paragraphs.map((p: any) => {
                          const citations = processQuoteCitations(p.content);
                          return {
                            ...p,
                            citations: citations
                          };
                        });
                        
                        // Fix findings citations - extract from embedded text
                        content.keyFindings.findings = content.keyFindings.findings.map((f: any) => {
                          const citations = processQuoteCitations(f.finding);
                          return {
                            ...f,
                            citations: citations
                          };
                        });
                        
                        // Fix recommendations citations - extract from embedded text
                        if (content.recommendations) {
                          content.recommendations = content.recommendations.map((r: any) => {
                            const actionCitations = processQuoteCitations(r.action);
                            const rationaleCitations = processQuoteCitations(r.rationale);
                            return {
                              ...r,
                              supportingCitations: [...actionCitations, ...rationaleCitations]
                            };
                          });
                        }
                        
                        // Add missing citationIndex if not present
                        if (!content.citationIndex) {
                          content.citationIndex = {
                            totalCitations: workflowData?.citations?.length || 0,
                            documentSources: workflowData?.documents?.map(doc => ({
                              docId: doc.id,
                              name: doc.name,
                              citationCount: 0,
                              baseUrl: ''
                            })) || []
                          };
                        }
                        
                        try {
                          return (
                            <UniversalJsonRenderer
                              data={content}
                              onCitationClick={handleUniversalCitationClick}
                              hideCitations={hideCitations}
                              customerName={workflowData?.customerName}
                              documents={workflowData?.documents || []}
                              customPdfExportHandler={handleExportPDF}
                              workflowType={workflowData?.workflowType}
                              compactHeader={true}
                              exportPortalRef={exportSlotRef}
                            />
                          );
                        } catch (renderError) {
                          console.error('UniversalJsonRenderer failed:', renderError);
                          return (
                            <div className="bg-red-50 border border-red-200 rounded p-4">
                              <h3 className="text-lg font-bold text-red-800 mb-2">Rendering Error</h3>
                              <p className="text-red-700 mb-4">The report data could not be rendered. Showing raw data instead:</p>
                              <pre className="whitespace-pre-wrap bg-gray-100 p-4 rounded text-sm overflow-auto max-h-96">
                                {JSON.stringify(content, null, 2)}
                              </pre>
                            </div>
                          );
                        }
                      } else {
                        // Fallback for non-matching structure
                        return (
                          <div>
                            <h3 className="text-lg font-bold mb-4">Analysis Results</h3>
                            <pre className="whitespace-pre-wrap bg-gray-100 p-4 rounded">
                              {JSON.stringify(content, null, 2)}
                            </pre>
                          </div>
                        );
                      }
                    })()
                  ) : (
                    typeof workflowData?.content === 'string' ? (
                      (() => {
                        console.log('🔍 String content detected, calling renderContentWithCitations');
                        console.log('🔍 String content preview:', workflowData?.content.substring(0, 300));
                        return renderContentWithCitations(workflowData?.content);
                      })()
                    ) : (
                      // ULTIMATE FALLBACK - Always show something
                      workflowData?.content ? (
                        <div className="space-y-4">
                          <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                            <h3 className="text-yellow-800 font-medium">📊 Report Content</h3>
                            <p className="text-yellow-600 text-sm mt-1">The report contains structured data.</p>
                          </div>
                          <div className="bg-white border border-gray-200 rounded-lg p-6">
                            <pre className="whitespace-pre-wrap text-sm text-gray-700 overflow-auto max-h-96">
                              {JSON.stringify(workflowData?.content, null, 2)}
                            </pre>
                          </div>
                        </div>
                      ) : (
                        // NO CONTENT AT ALL - Show loading message
                        <div className="space-y-4">
                          <div className="bg-blue-50 border border-blue-200 rounded p-6">
                            <h3 className="text-blue-800 font-medium mb-2">Processing Report...</h3>
                            <p className="text-blue-600 text-sm mb-4">
                              {workflowData?.workflowType === 'research_tool' 
                                ? 'The research analysis is being processed. This may take a moment.'
                                : 'The workflow report is being generated. Please wait...'}
                            </p>
                            <div className="flex justify-center mb-4">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                            <button
                              onClick={() => window.location.reload()}
                              className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                            >
                              Refresh to Check Progress
                            </button>
                          </div>
                          <div className="text-sm text-gray-600">
                            <p>Workflow Type: {workflowData?.workflowType || 'Unknown'}</p>
                            <p>Customer: {workflowData?.customerName || 'Unknown'}</p>
                            <p>Documents: {workflowData?.documents?.length || 0}</p>
                          </div>
                        </div>
                      )
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Chat & Documents (1/3 width). Hidden when railOpen=false. */}
        {railOpen && (
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <h4 className="text-xs font-medium text-secondary dark:text-dark-text mb-2 uppercase tracking-wide">Quick Actions</h4>
            <div className="space-y-1.5">
              {/* Regenerate Report Button */}
              <button
                onClick={() => setShowRegenerateModal(true)}
                disabled={isRegenerating || !canRegenerate}
                title={
                  canRegenerate
                    ? undefined
                    : `${getWorkflowTitle()} reports cannot be regenerated \u2014 run the workflow again from the project instead.`
                }
                className="w-full px-2.5 py-1.5 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
              >
                {isRegenerating ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Regenerating{regenerateProgress > 0 ? `… ${regenerateProgress}%` : '…'}
                  </>
                ) : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 4V1L8 5L12 9V6C15.31 6 18 8.69 18 12C18 15.31 15.31 18 12 18C8.69 18 6 15.31 6 12H4C4 16.42 7.58 20 12 20C16.42 20 20 16.42 20 12C20 7.58 16.42 4 12 4Z"/>
                    </svg>
                    Regenerate
                  </>
                )}
              </button>

              {/* Generate Proposal — only when we have enough context */}
              {workflowData?.reportId && workflowData?.workflowType && workflowData?.customerId && (
                <button
                  onClick={() => setShowGenerateProposal(true)}
                  className="w-full px-2.5 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors inline-flex items-center justify-center gap-1.5"
                  title="Generate a client-facing proposal from this report"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M9,13V15H15V13H9M9,17V19H12V17H9M9,9V11H15V9H9Z" />
                  </svg>
                  Generate Proposal
                </button>
              )}

              <button
                onClick={() => {
                  if (workflowData?.projectId && workflowData?.customerId) {
                    navigate(`/?customerId=${workflowData?.customerId}&projectId=${workflowData?.projectId}&view=project-details`);
                  } else if (workflowData?.customerId) {
                    navigate(`/?customerId=${workflowData?.customerId}`);
                  } else {
                    navigate('/');
                  }
                }}
                className="w-full px-2.5 py-1.5 text-xs font-medium bg-primary text-white rounded hover:bg-blue-600 transition-colors"
              >
                Back to Project Folder
              </button>

              {/* Attach to AMS360 Button - only show if AMS360 enabled AND customer exists */}
              {isAms360Enabled && workflowData?.customerId && (
                <button
                  onClick={() => setShowAMS360AttachModal(true)}
                  className="w-full px-2.5 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors inline-flex items-center justify-center gap-1.5"
                  title="Attach this report to an AMS360 activity"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1"/>
                    <polyline points="8,8 12,4 16,8"/>
                    <line x1="12" y1="4" x2="12" y2="16"/>
                  </svg>
                  Attach to AMS360
                </button>
              )}
            </div>
          </div>


          {/* Additional Document Upload */}
          <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="text-sm font-medium text-secondary dark:text-dark-text mb-3">Add Context Documents</h4>
            {workflowData?.projectId ? (
              <DocumentDropZone
                onDocumentsAdded={handleAdditionalDocuments}
                onDocumentRemoved={handleAdditionalDocumentRemoved}
                customerId={workflowData?.customerId}
                projectId={workflowData?.projectId}
                maxFiles={5}
                maxFileSize={50}
              />
            ) : (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Project Required for Document Upload
                    </p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                      To upload additional documents, please access this report from within a project context.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Chat Interface */}
          <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden flex-1 flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-secondary dark:text-dark-text">Ask Questions</h4>
                  {additionalDocuments.length > 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {additionalDocuments.filter(doc => doc.status === 'completed').length} additional document(s) in context
                    </p>
                  )}
                </div>
                {/* New Chat Button - only show if there are messages */}
                {chatHistory.length > 0 && (
                  <button
                    onClick={handleStartNewChat}
                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1"
                    title="Start a new conversation"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 4V1L8 5L12 9V6C15.31 6 18 8.69 18 12C18 15.31 15.31 18 12 18C8.69 18 6 15.31 6 12H4C4 16.42 7.58 20 12 20C16.42 20 20 16.42 20 12C20 7.58 16.42 4 12 4Z"/>
                    </svg>
                    New Chat
                  </button>
                )}
              </div>
            </div>

            {/* Past Conversations Strip */}
            <ReportConversationsStrip
              reportId={workflowData?.reportId}
              activeSessionId={currentSessionId}
              onSelectSession={handleSelectConversation}
              refreshKey={`${currentSessionId || 'none'}|${sessionTitle || ''}`}
            />

            {/* Sources picker — compact chip + token estimate */}
            <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center">
              <ChatContextViewer
                contextStats={contextStats}
                reportContent={(() => {
                  // Prepare report content same way as chat message
                  if (workflowData?.content) {
                    if (typeof workflowData?.content === 'string') {
                      return workflowData?.content;
                    } else {
                      // Handle JSON report content
                      return JSON.stringify(workflowData?.content, null, 2);
                    }
                  }
                  return '';
                })()}
                documents={[
                  ...(workflowData?.documents || []).map(doc => ({id: doc.id, name: doc.name})),
                  ...additionalDocuments.filter(doc => doc.status === 'completed').map(doc => ({id: doc.id, name: doc.name}))
                ]}
                includedDocuments={includedDocuments}
                pastReports={pastReports}
                includedPastReports={includedPastReports}
                chatHistory={chatHistory}
                onToggleReport={handleToggleReport}
                onToggleDocument={handleToggleDocument}
                onTogglePastReport={handleTogglePastReport}
                onToggleChatHistory={handleToggleChatHistory}
                className="mb-0"
              />
            </div>
            <div className="flex-1 min-h-[300px]">
              <ChatInterface
                messages={chatHistory}
                onSendMessage={handleSendChatMessage}
                isLoading={false}
                documents={[
                  ...(workflowData?.documents || []).map(doc => ({id: doc.id, name: doc.name})),
                  ...additionalDocuments.filter(doc => doc.status === 'completed').map(doc => ({id: doc.id, name: doc.name}))
                ]}
                className="h-full"
                onClearChat={handleClearChatHistory}
                saveStatus={saveStatus}
                sessionTitle={sessionTitle}
              />
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Click outside to close export menu */}
      {showExportMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowExportMenu(false)}
        />
      )}

      {/* HTML PDF Viewer for Citations */}
      {showHtmlPdfViewer && htmlViewerCitation && htmlViewerDocumentUrl && (
        <HtmlPdfViewer
          documentUrl={htmlViewerDocumentUrl}
          citation={{
            documentId: htmlViewerCitation.documentId || '',
            documentName: htmlViewerCitation.documentName || 'Unknown Document',
            page: htmlViewerCitation.page || 1,
            startOffset: htmlViewerCitation.startOffset || 0,
            endOffset: htmlViewerCitation.endOffset || 0,
            text: htmlViewerCitation.text || '',
            context: htmlViewerCitation.context
          }}
          allCitations={allCitationsOnPage}
          onClose={() => {
            setShowHtmlPdfViewer(false);
            setHtmlViewerCitation(null);
            setHtmlViewerDocumentUrl('');
            setAllCitationsOnPage([]);
            setClickedCitation(null);
            setHighlight(null);
          }}
        />
      )}

      {/* Unified PDF Citation Viewer */}
      {showPdfViewer && selectedCitation && (
        <UnifiedPdfCitationViewer
          documentId={selectedCitation.documentId}
          citation={{
            documentId: selectedCitation.documentId,
            documentName: selectedCitation.documentName || 'Unknown Document',
            page: selectedCitation.page || 1,
            startOffset: selectedCitation.startOffset || 0,
            endOffset: selectedCitation.endOffset || 0,
            text: selectedCitation.text || '',
            confidence: selectedCitation.confidence,
            context: `Citation from ${selectedCitation.documentName || 'Unknown Document'}, page ${selectedCitation.page || 1}`
          }}
          siblings={selectedCitation.siblings}
          siblingIndex={selectedCitation.siblingIndex}
          onNavigate={(s) => {
            // Replay the click flow on the requested sibling.
            const doc = resolveCitationDocument(s.docId, s.docName);
            if (!doc) return;
            setSelectedCitation((prev: any) => ({
              ...prev,
              documentId: doc.id,
              documentName: s.docName,
              page: typeof s.page === 'string' ? parseInt(s.page) : s.page,
              text: s.text || prev?.text || '',
              confidence: s.confidence,
              siblingIndex: s.sectionSiblingIndex,
            }));
          }}
          onClose={() => {
            setShowPdfViewer(false);
            setSelectedCitation(null);
          }}
          useAnnotationViewer={true}
        />
      )}

      {/* Selective Regenerate Report Modal */}
      {workflowData && (
        <SelectiveRegenerateModal
          isOpen={showRegenerateModal}
          onClose={() => setShowRegenerateModal(false)}
          onRegenerate={handleRegenerateReport}
          isRegenerating={isRegenerating}
          workflowData={workflowData}
          additionalDocuments={additionalDocuments}
          chatHistory={chatHistory}
        />
      )}

      {/* Generate Proposal Modal */}
      {showGenerateProposal && workflowData?.reportId && workflowData?.workflowType && workflowData?.customerId && (
        <GenerateProposalModal
          reportIds={[workflowData.reportId]}
          workflowType={workflowData.workflowType}
          customerId={workflowData.customerId}
          projectId={workflowData.projectId}
          token={localStorage.getItem('riskninja-token')}
          onClose={() => setShowGenerateProposal(false)}
        />
      )}

      {/* AMS360 Attach Report Modal */}
      {workflowData?.customerId && (
        <AMS360AttachReportModal
          isOpen={showAMS360AttachModal}
          onClose={() => setShowAMS360AttachModal(false)}
          customerId={workflowData.customerId}
          customerName={workflowData.customerName || 'Customer'}
          reportTitle={workflowData.reportTitle || `${workflowData.workflowType || 'Report'} - ${new Date().toLocaleDateString()}`}
          reportContent={typeof workflowData.content === 'string' ? workflowData.content : JSON.stringify(workflowData.content, null, 2)}
          workflowType={workflowData.workflowType}
          onGeneratePdf={generatePdfBase64}
        />
      )}

      {/* Letter Export Flow */}
      {workflowData && (
        <LetterExportFlow
          isOpen={showLetterExportFlow}
          onClose={() => setShowLetterExportFlow(false)}
          reportData={{
            customerName: workflowData.customerName,
            reportTitle: workflowData.reportTitle || getWorkflowTitle(),
            workflowType: workflowData.workflowType,
            createdAt: (workflowData as any).createdAt,
            projectName: (workflowData as any).projectName,
            projectId: workflowData.projectId,
            customerId: workflowData.customerId,
            reportId: workflowData.reportId,
            documents: workflowData.documents,
            universalJsonResponse: workflowData.universalJsonResponse,
            content: workflowData.content,
          }}
        />
      )}
        </div>
        {/* End Main Content Area grid */}
      </div>
      {/* End outer flex container */}
    </>
  );
  } catch (error) {
    // ULTIMATE SAFETY NET - If ANYTHING fails, show an error page instead of blank
    console.error('WorkflowReport render error:', error);
    return (
      <div className="w-full min-h-screen bg-slate-50 dark:bg-dark-bg p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8">
            <div className="text-center">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-red-800 mb-2">
                Report Display Error
              </h2>
              <p className="text-red-600 mb-4">
                An error occurred while displaying the report. This is likely a temporary issue.
              </p>
              <div className="space-y-3 mt-6">
                <button
                  onClick={() => window.location.reload()}
                  className="w-full max-w-xs mx-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Reload Page
                </button>
                <button
                  onClick={() => {
                    if (workflowData?.projectId && workflowData?.customerId) {
                      window.location.href = `/?customerId=${workflowData?.customerId}&projectId=${workflowData?.projectId}&view=project-details`;
                    } else if (workflowData?.customerId) {
                      window.location.href = `/?customerId=${workflowData?.customerId}`;
                    } else {
                      window.location.href = '/';
                    }
                  }}
                  className="w-full max-w-xs mx-auto px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Back to Project Folder
                </button>
              </div>
              <details className="mt-6 text-left max-w-md mx-auto">
                <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                  Technical Details
                </summary>
                <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto">
                  {error instanceof Error ? error.stack : String(error)}
                </pre>
              </details>
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default WorkflowReport;